/**
 * Plain-English review-card wording. Pure: no DOM, no CSS.
 *
 * Direction follows pipeline/moov_wallet.py (gross > 0 is "in", else "out").
 * The queue lanes import this file. This module does not import them.
 */

import {
  amVisibleDisplayText,
  amVisibleMultilineDisplayText,
} from "../../../ingest-worker/src/am_visible_text.js";
import { matchTextCandidates } from "../match_text.js";
import {
  MOOV_CARD_SUBJECT_PREFIX,
  type AccountPath,
  type CompanySlug,
  type QueueItem,
  type StaffRole,
} from "../types.js";
import {
  holdReasonFromRationale,
  type HoldReasonCode,
} from "../../../ingest-worker/src/hold_reasons.js";
import { decimalToCents, formatCents } from "./money.js";
import {
  ACCOUNT_UNKNOWN_TEXT,
  COMPANY_UNKNOWN_TEXT,
  accountLabel,
  accountLeafLabel,
  monthText,
  type AccountLabelContext,
} from "./display-words.js";
import { HOLD_SENTENCE, HOLD_WORDING, holdHappened, type HoldWording } from "./hold-wording.js";
import { advice } from "./capabilities.js";
import * as face from "./face.js";
import type { CompanyDirectory } from "./face.js";
import { navLabel, sentence } from "./sentences.js";

/**
 * Round-2 review, MUST-FIX 1 (orchestrator's wording). A fee top-up whose
 * amount disagrees with the fee the books expect is NOT always unbooked: on
 * R2b's replay path a matching top-up books on a later run. So this reason
 * states the disagreement and the one thing a reader can check, and claims
 * nothing about whether money moved. One sentence, every surface.
 */
export const FEE_TOPUP_AMOUNT_MISMATCH_COPY =
  "A fee top-up does not match the fee the books expect. Check the books before closing this card.";

export const MOOV_BREAKDOWN_REASON_COPY: Readonly<Record<string, string>> = {
  breakdown_mixed_kinds:
    "This payment mixes a deposit with rent, fees, money paid ahead or a card fee. Deposits are never on the same payment as those, so it was not booked.",
  breakdown_deposit_not_in_trust_wallet:
    "This payment is a deposit, but it arrived in a property company's wallet. Deposits book only in the trust company.",
  breakdown_income_in_trust_wallet:
    "This payment is rent, fees or money paid ahead, but it arrived in a trust wallet. Those book only in the property company.",
  breakdown_sum_mismatch:
    "The amounts the tenant portal listed do not add up to the payment amount.",
  breakdown_unreadable: "The tenant portal's amounts for this payment could not be read.",
  breakdown_version_unknown:
    "The tenant portal sent this payment in a format Books does not know yet.",
  breakdown_lease_number_invalid:
    "The lease number on this payment is missing or not in the tenant portal's format, so it was not booked.",
  breakdown_account_not_in_chart:
    "An account this payment needs is not open in the books yet. Likely ones: Tenant Credits, or Security Deposits and Pet Deposits in a trust company.",
  breakdown_period_locked: "The month this payment belongs to is closed.",
  breakdown_booked_then_reversed:
    "This payment was booked, and the payment processor now shows it reversed or returned. Nothing was changed in the books automatically.",
  property_not_owned:
    "The property named on this payment is not one this company owns, so it was not booked here.",
  property_not_configured:
    "The property named on this payment is not set up as a location in the books yet.",
  tenant_split_booking_failed:
    "Booking this payment failed. It was not booked.",
  // TFT-1b (owner 2026-10-24). A trust deposit's Moov fee is owed back by the property
  // company, and that company's operating wallet repays it with a fee top-up. These are
  // the cards on the DEPOSIT. Keep the codes in step with `FEE_TOPUP_CARD_REASONS` in
  // `pipeline/moov_wallet.py`.
  fee_row_missing:
    "The processor fee for this trust deposit has not settled yet, so the deposit was not booked. When the fee settles, the deposit books itself; if the fee failed, its own card says so.",
  breakdown_operating_company_unknown:
    "Books could not tell which property company owns this deposit's property, so it was not booked. The processor fee on a trust deposit is owed back by that company.",
  fee_topup_missing:
    "The processor fee on this trust deposit is still owed by the property company, and the top-up that repays it has not arrived. Nothing was changed in the books.",
  fee_receivable_deposit_reversed:
    "This deposit was returned while its processor fee was still owed by the property company, so no top-up will repay it. Nothing was changed in the books. Whether to write the fee off is the account manager's decision.",
  // The cards on a fee top-up itself (either wallet). A top-up books itself, or not at all.
  fee_topup_deposit_unseen:
    "A fee top-up arrived, but the deposit it repays is not in the books yet.",
  fee_topup_counterpart_unseen:
    "A fee top-up was seen in one wallet, but the matching payment in the other wallet has not shown up.",
  // Round-2 review, MUST-FIX 1: "nothing was booked" is false on the replay
  // path, where R2b books a top-up whose amount the books already expect. This
  // reason says only what is true either way.
  fee_topup_amount_mismatch: FEE_TOPUP_AMOUNT_MISMATCH_COPY,
  fee_topup_duplicate:
    "A second top-up arrived for a deposit whose fee was already repaid. Money moved twice and the account manager has to recover it. Nothing was booked.",
  fee_topup_without_receivable:
    "A fee top-up arrived for a deposit that has no fee owed, because it was booked before the top-up switch date. Nothing was booked.",
  fee_topup_unreadable: "Books could not read the details of a fee top-up, so nothing was booked.",
  fee_topup_account_not_in_chart:
    "An account a fee top-up needs is not open in the books yet. Likely ones: the fee receivable for the property company in the trust company, or the processor fees expense in the property company.",
  fee_topup_location_unknown:
    "The property named on a fee top-up is not set up as a location in this company's books.",
  fee_topup_period_locked: "The month a fee top-up belongs to is closed.",
  fee_topup_booking_failed: "Booking a fee top-up failed. It was not booked.",
  // Refusals the Worker reaches after reading both wallets and the deposit.
  topup_has_fee:
    "The fee top-up transfer was itself charged a processor fee, which nobody has agreed how to book.",
  topup_amount_differs:
    "The amount of the fee top-up in the wallets is not the fee named in its details.",
  topup_not_usd: "A wallet row of a fee top-up is not in US dollars.",
  topup_rows_unexpected:
    "A fee top-up transfer shows more wallet rows than one payment out and one payment in.",
  topup_wrong_direction:
    "A fee top-up moved the wrong way. It must go from a property company's wallet to the trust wallet.",
  topup_not_trust_destination: "A fee top-up went to a wallet that is not the trust wallet.",
  topup_company_not_served:
    "A fee top-up involved a company that the trust company does not serve.",
  topup_property_not_owned:
    "The property named on a fee top-up is not one the paying company owns.",
  topup_deposit_not_a_deposit:
    "The transfer a fee top-up names is not a tenant deposit in the trust wallet.",
  topup_fee_differs:
    "The fee a top-up repays is not the processor fee taken on its deposit.",
  topup_lease_differs: "The lease on a fee top-up is not the lease on its deposit.",
  topup_property_differs: "The property on a fee top-up is not the property on its deposit.",
  topup_duplicate:
    "An earlier top-up for this deposit was read first and keeps the fee while it is live. If that one is refused, this top-up books; if both are good, money moved twice and the account manager has to recover one.",
  topup_pre_switch:
    "A fee top-up names a deposit from before the switch date, whose fee was booked as an expense and is not owed back. Nothing was booked on either side.",
  topup_transfer_not_completed:
    "The fee top-up transfer failed, was cancelled or was reversed by the processor, so nothing was booked.",
  fee_topup_receivable_closed:
    "A fee top-up arrived for a deposit whose fee had already been written off, so nothing was booked. The money moved, so the account manager has to decide what to do with it.",
};

/**
 * The one sentence for a cause code (rd r12 B1b-i). The table above is the
 * only home: the plan tables below read it instead of typing a second copy,
 * so a wording change happens once.
 */
function causeSentence(code: string): string {
  const words = MOOV_BREAKDOWN_REASON_COPY[code];
  if (words === undefined) {
    throw new Error(`card-presenter: no cause sentence for "${code}"`);
  }
  return words;
}

/** Lockstep with pipeline/config.py INTERCOMPANY_ACCOUNT_PATTERN via queue.ts. */
const BULK_INTERCOMPANY_ACCOUNT_PATTERN =
  /^(Assets|Liabilities):([A-Z][A-Z0-9]*):(Due-From|Due-To):([A-Z][A-Z0-9]*)$/;

const BULK_LOAN_PATH_SEGMENT = "Loans";

/** Boolean form of classify_booking_account with no own-bank exception. */
export function isJudgmentClassAccount(account: string): boolean {
  if (BULK_INTERCOMPANY_ACCOUNT_PATTERN.test(account)) return true;
  const parts = account.split(":");
  if (parts[0] === "Equity") return true;
  return parts.includes(BULK_LOAN_PATH_SEGMENT);
}

const BOOKING_FAILURE_LINE_PREFIX = "ARF1";
const HUB_APPLIED_LINE_PREFIX = "HGL1";
const LOAN_STATEMENT_LINE_PREFIX = "LS1";
const EVIDENCE_QUEUE_RATIONALE_PREFIX =
  "corroboration does not answer this booking (";

const RESERVED_PREFIXES = [
  BOOKING_FAILURE_LINE_PREFIX,
  HUB_APPLIED_LINE_PREFIX,
  LOAN_STATEMENT_LINE_PREFIX,
] as const;

function dropTrailingCR(line: string): string {
  return line.endsWith("\r") ? line.slice(0, -1) : line;
}

function isReservedRationaleLine(line: string): boolean {
  const body = dropTrailingCR(line);
  return RESERVED_PREFIXES.some(
    (prefix) => body === prefix || body.startsWith(`${prefix}\t`),
  );
}

/** Cause text from an ARF1 rationale, or null when the line is not ARF1. */
export function bookingFailureCause(text: string | undefined): string | null {
  if (!text) return null;
  const lines = text.split("\n");
  const first = dropTrailingCR(lines[0] ?? "");
  if (
    first !== BOOKING_FAILURE_LINE_PREFIX &&
    !first.startsWith(`${BOOKING_FAILURE_LINE_PREFIX}\t`)
  ) {
    return null;
  }
  return lines
    .slice(1)
    .filter((line) => !isReservedRationaleLine(line))
    .join("\n")
    .trim();
}

export function evidenceQueueWhy(text: string | undefined): string | null {
  if (!text) return null;
  const body = text
    .split("\n")
    .filter((line) => !isReservedRationaleLine(line))
    .join("\n")
    .trim();
  if (!body.startsWith(EVIDENCE_QUEUE_RATIONALE_PREFIX)) return null;
  const rest = body.slice(EVIDENCE_QUEUE_RATIONALE_PREFIX.length);
  const semi = rest.indexOf(";");
  if (semi <= 0) return null;
  return rest.slice(0, semi);
}

const HUB_BATCH_CENSUS_LABEL = "hub_batch_census";

export type UnmatchedHubCensusKind =
  | "missing"
  | "unparseable"
  | "zero"
  | "no_remaining_candidate"
  | "awaiting_candidate"
  | "remaining_candidate_exists";

function decodeCensusField(raw: string): string | null {
  if (raw.startsWith('"')) {
    try {
      const parsed: unknown = JSON.parse(raw);
      return typeof parsed === "string" ? parsed : null;
    } catch {
      return null;
    }
  }
  return raw;
}

function netStringIsZero(net: string): boolean | null {
  const match = /^(-)?(0|[1-9]\d{0,12})(?:\.(\d{1,2}))?$/.exec(net);
  if (match === null || match[2] === undefined) return null;
  const frac = (match[3] ?? "").padEnd(2, "0");
  return match[2] === "0" && frac === "00";
}

/** Kind only. Same decisions as queue.ts parseUnmatchedHubGlCensus, no logging. */
export function unmatchedHubCensusKind(body: string): UnmatchedHubCensusKind {
  const matches = [
    ...body.matchAll(
      new RegExp(
        `(?:^|\\n)${HUB_BATCH_CENSUS_LABEL}:\\s+hub_batch_net=(\\S+)\\s+legs=(\\S+)(?:\\s+remaining_candidates=(\\S+)\\s+consumed=(\\S+))?`,
        "g",
      ),
    ),
  ];
  if (matches.length === 0) return "missing";
  if (matches.length !== 1) return "unparseable";
  const match = matches[0];
  const netRaw = match?.[1];
  const legsRaw = match?.[2];
  const remainingRaw = match?.[3];
  const consumedRaw = match?.[4];
  if (netRaw === undefined || legsRaw === undefined) return "unparseable";
  const net = decodeCensusField(netRaw);
  const legsText = decodeCensusField(legsRaw);
  if (net === null || legsText === null || net === "unparseable") return "unparseable";
  const zero = netStringIsZero(net);
  const legs = /^[0-9]+$/.test(legsText) ? Number(legsText) : Number.NaN;
  if (zero === null || !Number.isInteger(legs)) return "unparseable";
  if (remainingRaw === undefined || consumedRaw === undefined) return "unparseable";
  const remainingText = decodeCensusField(remainingRaw);
  const consumedText = decodeCensusField(consumedRaw);
  if (remainingText === null || consumedText === null) return "unparseable";
  const remainingCandidates = /^[0-9]+$/.test(remainingText)
    ? Number(remainingText)
    : Number.NaN;
  if (!Number.isInteger(remainingCandidates) || !/^[01]$/.test(consumedText)) {
    return "unparseable";
  }
  if (zero) return "zero";
  if (remainingCandidates === 0 && consumedText === "1") return "no_remaining_candidate";
  if (remainingCandidates === 0) return "awaiting_candidate";
  return "remaining_candidate_exists";
}

export type CardChipTone =
  | "needs you"
  | "waiting on the system"
  | "waiting on the owner";

export interface CardChip {
  text: string;
  tone: CardChipTone;
}

export interface CardPresentation {
  title: string;
  happened: string;
  suggest: string;
  primaryLabel: string;
  secondary: readonly string[];
  chip: CardChip;
  family: string;
  groupKey: string;
  /**
   * Key for the chart chooser's "remember the last account/location" memory.
   * Unlike `groupKey` it keeps the exact amount, so choosing an account on
   * one payment is never preloaded on a payment of another size (Q-d: the
   * display group key dropped the amount, the memory must not).
   */
  memoryKey: string;
  disabledHint: string;
  details: readonly string[];
}

export interface CardPresenterContext {
  /**
   * The one directory of company names (B1c-1). A caller that has the whole
   * company read passes it and every company word on the card comes from
   * `face.company`. A caller that only holds the shell's name map passes
   * `companyLabels` instead and the directory is built from it, incomplete:
   * an unknown name then reads "this company", never the slug (decision 14).
   */
  directory?: CompanyDirectory;
  companyLabels?: ReadonlyMap<string, string>;
  /**
   * The shell's ledger segment -> company name read (`face.companySegmentNames`,
   * rd13 L1, review round 2). Absent in a unit test or a session that could not
   * read the segments; the label then falls back to the slug-derived segment.
   */
  companySegments?: ReadonlyMap<string, string>;
  role: StaffRole;
  /**
   * False only when the published chart is loaded and lists **no** location.
   * A wallet card asking for a location then has nothing to pick, so it is a
   * setup fix; with a picker (or an unloaded chart) it stays answerable here.
   * Absent means "not known", and nothing is reclassified on a guess.
   */
  hasSelectableLocations?: boolean;
  /**
   * The words for a suggested account when its plain words are shared with
   * another account of the same company ("Expenses › Repairs (RENT)"), or null
   * when they are not. Supplied by the page, which holds the company's chart;
   * absent means "not known" and the short leaf is used. A button that books X
   * must name X (round five F2).
   */
  accountWords?: (company: CompanySlug, account: AccountPath) => string | null;
}

/**
 * The card's ONE account-label context, built from the directory it already
 * keeps (rd13 L1): a card that names a between-companies account names the
 * company, never its internal key. `presenterDirectory` is the same value the
 * company words come from, so a card cannot word a company one way and an
 * account another.
 */
function presenterLabelContext(ctx: {
  directory?: CompanyDirectory;
  companyLabels?: ReadonlyMap<string, string>;
  companySegments?: ReadonlyMap<string, string>;
}): AccountLabelContext {
  // A caller's directory already carries the session's segment read; one built
  // here from the name map does not, so the explicit segments cover it.
  return face.accountLabelContext(
    ctx.directory !== undefined ? ctx.directory : presenterDirectory(ctx).names(),
    ctx.companySegments,
  );
}

/**
 * The one directory a card reads its company words from.
 *
 * B1c-1: a page that has the company read passes `directory`; a caller that
 * only has the shell's name map (an older test, a card rendered alone) still
 * works, and its directory is honestly incomplete — a company the map does not
 * name reads "this company", never `SYN-RENTALS` (decision 14).
 */
export function presenterDirectory(ctx: {
  directory?: CompanyDirectory;
  companyLabels?: ReadonlyMap<string, string>;
}): CompanyDirectory {
  if (ctx.directory !== undefined) return ctx.directory;
  return face.createCompanyDirectory(
    [...(ctx.companyLabels ?? new Map<string, string>())].map(([slug, displayName]) => ({
      slug,
      displayName,
    })),
    { complete: false },
  );
}

/**
 * An internal group key that carries a company slug.
 *
 * This is a KEY, never words: it is compared and used as a Map key, and it is
 * written into a `data-group-key` attribute (a value a person does not read).
 * The brand guard cannot tell a key from a face, so this is the one place in
 * the group that turns a slug into text, and it is a recorded correct-use site.
 */
export function slugKey(company: CompanySlug, rest: string): string {
  return `${company}|${rest}`;
}

/** The short name of a suggested account, told apart from a twin when the page knows of one. */
function suggestedAccountWords(
  item: QueueItem,
  ctx: CardPresenterContext,
  account: string | undefined,
): string | null {
  if (account === undefined || account.length === 0) return null;
  return (
    ctx.accountWords?.(item.company, account) ??
    accountLeaf(account, presenterLabelContext(ctx))
  );
}

type MoovMode =
  | "account"
  | "choose"
  | "confirm"
  | "location"
  | "wait"
  | "blocked"
  | "none"
  | "refused";

interface MoovPlan {
  happened: string;
  amAction: string;
  ownerAction: string;
  amPrimary: string;
  ownerPrimary: string;
  mode: MoovMode;
  hint: string;
  title?: string;
  idlePrimary?: string;
  detail?: string;
}

interface MoovTypeInfo {
  key: string;
  noun: string;
  plural: string;
  usually: string;
}

const PAYOUT: MoovTypeInfo = {
  key: "payout",
  noun: "payout",
  plural: "payouts",
  usually: "Payouts usually go to this company's own bank account.",
};
const TOP_UP: MoovTypeInfo = {
  key: "top-up",
  noun: "top-up",
  plural: "top-ups",
  usually: "Top-ups usually come from this company's own bank account.",
};
const CASH_OUT: MoovTypeInfo = {
  key: "cash-out",
  noun: "cash-out",
  plural: "cash-outs",
  usually: "Cash-outs usually go to this company's own bank account.",
};
const MOOV_FEE: MoovTypeInfo = {
  key: "moov-fee",
  noun: "Processor fee",
  plural: "Processor fees",
  usually: "Processor fees usually go to this company's bank fees.",
};
const WALLET_MOVEMENT: MoovTypeInfo = {
  key: "wallet-movement",
  noun: "wallet movement",
  plural: "wallet movements",
  usually: "Wallet movements usually go to the account you choose for this type.",
};

/** The next step on a card that needs an account picked; `queue.ts` reads the
 * same words (rd r12 B1b-i), so this is their one home. */
export const ACCOUNT_HINT = "Pick an account to book this to.";
/**
 * The one chip word for a card that cannot be answered in the console
 * (docs/design/rd-polish-rules.md §1: "Setup fix"). A closed month has its own
 * word (Y5 brief item 2) because the state is the month, not a setting.
 */
export const SETUP_FIX_CHIP = "Setup fix";
/** The one chip word for a card held by a closed month (Y5 brief item 2). */
export const MONTH_CLOSED_CHIP = "Month closed";
/** The only next step a closed-month card offers: it stays where it is. */
export const CLOSED_MONTH_NEXT_STEP = "Skip for now leaves it on this list.";
/**
 * What "this" is, before decision 2's sentence (B1b-ii review LOW-1).
 *
 * The decided sentence is "This has to be added in the ledger by hand for now."
 * and it stays verbatim; on its own it does not say WHAT has to be added, so
 * the action it is about comes first. One home, read by the closed-month card
 * and the period-locked retry sentence so the two cannot drift.
 */
export const REOPEN_MONTH_BY_HAND_PREFIX =
  "Reopening the month is not possible in the console.";

/**
 * DECIDED (owner, round-12 decision 2): reopening a month is a ledger change
 * nobody can do in the console, so the next step is the decided sentence. Kept
 * out of `closedMonthSentence` so the fact stays within the card's 20-word
 * face (y5 pins it) instead of being clipped mid-sentence.
 */
export const CLOSED_MONTH_ACTION = `${REOPEN_MONTH_BY_HAND_PREFIX} ${advice(
  "reopen_month",
  "account_manager",
)} ${CLOSED_MONTH_NEXT_STEP}`;
/**
 * The account manager's half of a role-split card, ONCE per wording (rd r12
 * B1b-ii, E-B5). No capability row can model an owner action taken outside
 * the console, so these stay role-split; the role-advice guard cannot tell a
 * table cell from advice, so each is listed as a correct-use entry. The
 * decision-2 actions (open an account, add a place, map a bank, reopen a
 * month) do NOT live here: they read `advice(...)`.
 */
export const ASK_OWNER_SENTENCE = "Ask the owner.";
export const ASK_OWNER_LABEL = "Ask the owner";
export const FEE_WAIT_AM = "Wait for the fee to post, or ask the owner.";
export const AUTO_BOOK_AM =
  "Approve to book it, or ask the owner to turn automatic booking on.";
export const TELL_OWNER_AM = "Try again. If it keeps failing, tell the owner.";
export const CHOOSE_AGAIN_AM = "Choose again, or ask the owner.";
export const PICK_ACCOUNT_AM = "Pick the account, or ask the owner.";
export const FAILED_FEE_AM =
  "A failed fee cannot post; nothing here can retry it — ask the owner to look at it with the payment processor.";
export const PROCESSOR_AMOUNTS_AM =
  "Ask the owner to check those amounts with the payment processor.";
export const CORRECT_IT_AM = "Ask the owner how to correct it.";
export const TRUST_DEPOSIT_AM =
  "Ask the owner to move this deposit into the trust wallet; nothing here can move it.";
export const TRUST_MONEY_AM =
  "Ask the owner to move this money out of the trust wallet; nothing here can move it.";
export const FORMAT_AM =
  "Ask the owner to have the books support this format; sending it again will not help.";
export const FINALLY_LOOK_AM =
  "Nothing to do yet. If it keeps failing, ask the owner to look.";
export const MAKE_CHANGE_AM = "Ask the owner to make the change in the books.";
export const DEAL_WITH_IT_AM =
  "Nothing here can book it. Ask the owner to deal with it, or close it with a note.";
export const REVIEW_STATEMENT_AM =
  "Ask the owner to review the published statement.";
export const ASK_LOOK_AT_THIS_AM = "Ask the owner to look at this.";
export const ASK_LOOK_AM = "Ask the owner to look.";
export const REMOVAL_ESCALATE_AM =
  "Ask the owner, or decline to close it with no reversal.";
/** Decision 2's sentence for each action that has no console control. */
const LEDGER_OPEN_ACCOUNT = advice("open_account", "account_manager");
const LEDGER_MAP_BANK = advice("map_bank", "account_manager");
const LEDGER_ADD_PLACE = advice("add_place", "account_manager");

/**
 * R4-26, decided by the owner (2026-10-30, "I am fine with your plan."): the
 * first bank account of a new company. The decided sentence lives in
 * `ui/sentences.ts` (`first_bank_mapping`); this keeps the exported name its
 * callers and pins already use (rd r12 B1b-i).
 */
export const FIRST_BANK_MAPPING_COPY = sentence("first_bank_mapping", "account_manager");
/**
 * Round-3 review, MUST-FIX 1 (orchestrator's wording). The escrow card's fact,
 * true of every `direction` the Worker writes — `ledger_higher` (the books are
 * higher), `ledger_lower` (they are lower) and `uncheckable` (a figure could not
 * be read). The card carries a Record disbursement control, so the face states
 * the disagreement and leaves the next step to the card's own control.
 */
export const ESCROW_BALANCE_FACE_COPY =
  "The escrow balance in the books and the servicer's figure do not agree, or the figure could not be read.";
/**
 * Round-3 review, MUST-FIX 2 (orchestrator's wording). `Missing Plaid
 * account_id` is ONE bank line that arrived with no account on it. Connecting
 * an account never clears it, so it must not borrow {@link FIRST_BANK_MAPPING_COPY}
 * — that sentence belongs to `Unmapped Plaid account` alone.
 */
export const MISSING_ACCOUNT_FACE_COPY =
  "A bank line arrived with no account on it, so it was not booked. It can't be fixed from the console.";
/** What happened, as the missing-account card's own fact. */
export const MISSING_ACCOUNT_HAPPENED = "This line was not booked.";
/** What happened on a closed-month card, when the month itself is unknown. */
const CLOSED_MONTH_HEAD = HOLD_SENTENCE.monthClosed;
const NO_SINGLE_ACCOUNT = "This cannot be booked to one account.";
const NOTHING_YET = "There is nothing to approve yet.";
/** The bank-text placeholder card's only fact; `queue.ts` reads the same words
 * (rd r12 B1b-i), so this is their one home. */
export const BANK_TEXT_NOT_ENOUGH_COPY =
  "The bank text alone is not enough to book this ourselves.";
/** The "write what you found" hint on a refused card; `queue.ts` reads the same
 * words (rd r12 B1b-i). */
export const WRITE_FINDING_HINT = "Write what you found before booking.";
/**
 * The generic next step for a card whose fix is a change in the company's
 * books. It names the books, not a page: no console screen opens a location,
 * an account, a hub account list or a wallet list (round-1 review, MUST-FIX 1).
 */
const OWNER_SETUP = "This needs a change in the books.";
/** The chip word for the state; `docs/design/rd-polish-rules.md` §1 decides it. */
const OWNER_CHIP = SETUP_FIX_CHIP;

function accountPlan(
  happened: string,
  amAction: string,
  ownerAction: string,
  extra?: Partial<MoovPlan>,
): MoovPlan {
  return {
    happened,
    amAction,
    ownerAction,
    amPrimary: "Book it",
    ownerPrimary: "Book it",
    mode: "account",
    hint: ACCOUNT_HINT,
    ...extra,
  };
}

function blockedPlan(
  happened: string,
  amAction: string,
  ownerAction: string,
  amPrimary: string,
  ownerPrimary: string,
  hint: string,
  extra?: Partial<MoovPlan>,
): MoovPlan {
  return {
    happened,
    amAction,
    ownerAction,
    amPrimary,
    ownerPrimary,
    mode: "blocked",
    hint,
    ...extra,
  };
}

function nonePlan(
  happened: string,
  ownerPrimary: string,
  title?: string,
): MoovPlan {
  return {
    happened,
    amAction: ASK_OWNER_SENTENCE,
    ownerAction: ownerPrimary.endsWith(".") ? ownerPrimary : `${ownerPrimary}.`,
    amPrimary: ASK_OWNER_LABEL,
    ownerPrimary,
    mode: "none",
    hint: NO_SINGLE_ACCOUNT,
    ...(title === undefined ? {} : { title }),
  };
}

function refusedPlan(happened: string): MoovPlan {
  return {
    happened,
    amAction: "Choose the category and write what you found.",
    ownerAction: "Choose the category and write what you found.",
    amPrimary: "Choose the category",
    ownerPrimary: "Choose the category",
    mode: "refused",
    hint: WRITE_FINDING_HINT,
  };
}

const FEE_PLAN: MoovPlan = {
  happened: "The processor fee for this payout is missing or does not match.",
  amAction: FEE_WAIT_AM,
  ownerAction: "Wait for the fee to post, or check the fee.",
  amPrimary: "Nothing to do yet",
  ownerPrimary: "Nothing to do yet",
  mode: "wait",
  hint: NOTHING_YET,
};

const TRANSFER_WAIT: MoovPlan = {
  happened: "This transfer has not finished, or it failed.",
  amAction: "Nothing to do yet.",
  ownerAction: "Nothing to do yet.",
  amPrimary: "Nothing to do yet",
  ownerPrimary: "Nothing to do yet",
  mode: "wait",
  hint: NOTHING_YET,
};

const OPEN_ACCOUNT: MoovPlan = blockedPlan(
  "The account this should use is not open in the books yet.",
  LEDGER_OPEN_ACCOUNT,
  LEDGER_OPEN_ACCOUNT,
  "Nothing to do yet",
  "Nothing to do yet",
  "The account is not open in the books yet.",
);

/**
 * A closed month (Y5 brief item 2). No account choice can book it, and the
 * reopen step is not a control on this card, so the fact is said once
 * (`closedMonthSentence`) and decision 2's sentence is the next step.
 */
function closedMonthPlan(): MoovPlan {
  return {
    happened: CLOSED_MONTH_HEAD,
    amAction: CLOSED_MONTH_ACTION,
    ownerAction: CLOSED_MONTH_ACTION,
    amPrimary: "Nothing to do yet",
    ownerPrimary: "Nothing to do yet",
    mode: "blocked",
    hint: "",
  };
}

/** The planned wallet reasons, exported so the role and wording sweeps can
 * derive their population from the table itself (never a hand list). */
export const MOOV_PLANS: Readonly<Record<string, MoovPlan>> = {
  no_rule: accountPlan(
    "A payout has not been booked. No rule covers payouts yet.",
    HOLD_SENTENCE.pickAccount,
    "Pick the account it belongs to.",
    { detail: "Nothing in the books says where this money belongs yet." },
  ),
  auto_commit_off: accountPlan(
    "We know where this goes but automatic booking is off for this company.",
    AUTO_BOOK_AM,
    "Approve to book it, or turn automatic booking on.",
  ),
  ambiguous_rules: accountPlan(
    "Two of your rules both match this, so we did not guess.",
    "Pick the account.",
    "Pick the account.",
  ),
  account_not_in_chart: OPEN_ACCOUNT,
  fee_account_not_in_chart: blockedPlan(
    "The processor fee account is not open in the books yet.",
    LEDGER_OPEN_ACCOUNT,
    LEDGER_OPEN_ACCOUNT,
    "Nothing to do yet",
    "Nothing to do yet",
    "The account is not open in the books yet.",
  ),
  period_locked: closedMonthPlan(),
  movement_booking_failed: accountPlan(
    "We tried to book this and the write failed. Nothing was booked.",
    TELL_OWNER_AM,
    "Try again. If it keeps failing, check the books.",
  ),
  location_not_chosen: {
    happened: "This needs a location before it can be booked.",
    amAction: "Pick the location.",
    ownerAction: "Pick the location.",
    amPrimary: "Pick the location",
    ownerPrimary: "Pick the location",
    mode: "location",
    hint: "Pick the location.",
  },
  approval_booking_failed: accountPlan(
    "Your answer could not be booked.",
    CHOOSE_AGAIN_AM,
    "Choose again.",
    { idlePrimary: "Choose again" },
  ),
  // TFT-1b: the system-only receivable cannot be picked by hand, but the card
  // is answered by choosing another account. It is not a blocked reason.
  approval_account_system_only: accountPlan(
    "That account is reserved for the system.",
    "Choose another account.",
    "Choose another account.",
    { idlePrimary: "Choose another account" },
  ),
  // R2b (R2-09, `pipeline/moov_wallet.py`): an approval to the wallet's own
  // cash account nets the two legs on one account, so nothing is booked and
  // the movement is carded again. The card is answered by choosing another
  // account, exactly like the system-only refusal above — it is not a
  // dead end.
  approval_account_is_wallet: accountPlan(
    "That account is this payment's own wallet account, so booking it there would cancel itself out.",
    "Choose another account.",
    "Choose another account.",
    { idlePrimary: "Choose another account" },
  ),
  counterparty_unreadable: accountPlan(
    "We could not tell which company the other side of this transfer belongs to.",
    PICK_ACCOUNT_AM,
    "Pick the account.",
  ),
  intercompany_outside_set: blockedPlan(
    "One company is not set up for company-to-company bookings.",
    ASK_OWNER_SENTENCE,
    "Set up company-to-company bookings.",
    ASK_OWNER_LABEL,
    "Set up company-to-company bookings.",
    "Company-to-company booking is not set up.",
    {
      detail:
        "This looks like a transfer between two companies, but one is not set up for company-to-company bookings.",
    },
  ),
  transfer_pair_ambiguous: {
    happened: "More than one bank line could be the other half of this transfer.",
    amAction: "Pick the right one.",
    ownerAction: "Pick the right one.",
    amPrimary: "Pick the right one",
    ownerPrimary: "Pick the right one",
    mode: "choose",
    hint: "Pick the right one.",
  },
  anomaly: blockedPlan(
    "We refused to pair this transfer automatically.",
    ASK_OWNER_SENTENCE,
    "Review this transfer yourself.",
    ASK_OWNER_LABEL,
    "Review this transfer",
    "This transfer was not paired on its own.",
  ),
  fee_missing: FEE_PLAN,
  // Round 5 (should-fix 1): a FAILED fee row never settles (the Worker queues
  // the settled row whose transfer failed, `moov_wallet.ts` 1504-1506; a
  // failed transfer never settles), so "wait for the fee to post" promised a
  // post that cannot happen. It is a person's look, not a system wait.
  fee_not_success: {
    happened: "The processor fee on this payout failed, so it was not booked.",
    amAction: FAILED_FEE_AM,
    ownerAction:
      "A failed fee cannot post; nothing here can retry it — the fee has to be sorted out with the payment processor.",
    amPrimary: ASK_OWNER_LABEL,
    ownerPrimary: "Check the fee",
    mode: "blocked",
    hint: "A failed fee cannot post.",
  },
  // Round 6 (note 2): `amount_disagrees` reaches the card only after both rows
  // are completed, on a sign shape or on the bill's amount against the
  // payout's (`pipeline/moov_wallet.py` 541, 560) — a frozen fact. "Wait for
  // the fee to post" (FEE_PLAN) promised a post that cannot happen, so this is
  // a person's look, not a system wait: nothing here re-reads either number.
  amount_disagrees: {
    happened:
      "The payout, its fee and the bill do not agree on the amounts, so it was not booked.",
    amAction: PROCESSOR_AMOUNTS_AM,
    ownerAction:
      "The amounts have to be checked with the payment processor; nothing here can re-read them.",
    amPrimary: ASK_OWNER_LABEL,
    ownerPrimary: "Check the amounts",
    mode: "blocked",
    hint: "The amounts on this payout do not agree.",
  },
  payment_missing: accountPlan(
    "We cannot link this to a bill payment.",
    "Pick the account yourself.",
    "Pick the account yourself.",
  ),
  not_a_payout: accountPlan(
    "We cannot link this to a bill payment.",
    "Pick the account yourself.",
    "Pick the account yourself.",
  ),
  // V1: a hub bill paid from the Moov wallet is decided by
  // decide_bill_payment, and its hold code becomes this card's body. These six
  // are the codes only a bill can produce (BILL_HOLD_WALLET_CODES). The words
  // are the bill rows with "the wallet payment" where the card is not a bank
  // line, so the wallet card and the bill card explain the same hold.
  line_unlocated: accountPlan(
    "A line on this bill has no property, so the wallet payment did not book itself.",
    HOLD_SENTENCE.pickLocationAndAccount,
    HOLD_SENTENCE.pickLocationAndAccount,
    {
      amPrimary: "Pick the location",
      ownerPrimary: "Pick the location",
      idlePrimary: "Pick the location",
      hint: "Pick the location.",
    },
  ),
  location_not_in_ledger: accountPlan(
    HOLD_SENTENCE.billLocationNotSetUp,
    HOLD_SENTENCE.pickLocation,
    HOLD_SENTENCE.pickLocationOrAddProperty,
    {
      amPrimary: "Pick the location",
      ownerPrimary: "Pick the location",
      idlePrimary: "Pick the location",
      hint: "Pick the location.",
    },
  ),
  multiple_locations: accountPlan(
    "This bill covers several properties that do not share one location, so the wallet payment did not book itself.",
    HOLD_SENTENCE.pickOneAccountAndLocation,
    HOLD_SENTENCE.pickOneAccountAndLocation,
  ),
  unmapped_gl_account: accountPlan(
    HOLD_SENTENCE.billAccountNotListed,
    HOLD_SENTENCE.pickAccount,
    HOLD_SENTENCE.pickAccountAndAddHubAccount,
  ),
  line_maps_to_bank: accountPlan(
    HOLD_SENTENCE.billAccountIsOwnBank,
    HOLD_SENTENCE.pickRightAccount,
    HOLD_SENTENCE.pickRightAccountAndFixHubList,
  ),
  lookup_failed: accountPlan(
    "Books could not read the bill for this wallet payment.",
    HOLD_SENTENCE.pickAccountOrWait,
    HOLD_SENTENCE.pickAccountOrWait,
  ),
  transfer_not_completed: TRANSFER_WAIT,
  transfer_failed: TRANSFER_WAIT,
  fee_topup_deposit_unseen: {
    happened:
      "A fee top-up arrived in the property company's wallet, but the trust deposit it repays has not been seen yet.",
    amAction: "Nothing to do yet. This card ends by itself once the top-up is booked.",
    ownerAction: "Nothing to do yet. This card ends by itself once the top-up is booked.",
    amPrimary: "Nothing to do yet",
    ownerPrimary: "Nothing to do yet",
    mode: "wait",
    hint: NOTHING_YET,
  },
  fee_topup_counterpart_unseen: {
    happened:
      "One side of a fee top-up transfer has been seen, but the other wallet's side has not.",
    amAction: "Nothing to do yet. This card ends by itself once both sides are in and booked.",
    ownerAction: "Nothing to do yet. This card ends by itself once both sides are in and booked.",
    amPrimary: "Nothing to do yet",
    ownerPrimary: "Nothing to do yet",
    mode: "wait",
    hint: NOTHING_YET,
  },
  transfer_date_missing: {
    happened: "This transfer has no date yet.",
    amAction: "Nothing to do yet.",
    ownerAction: "Nothing to do yet.",
    amPrimary: "Nothing to do yet",
    ownerPrimary: "Nothing to do yet",
    mode: "wait",
    hint: NOTHING_YET,
  },
  judgment_account: {
    happened: "This needs a person to confirm the account.",
    amAction: HOLD_SENTENCE.confirmAccount,
    ownerAction: HOLD_SENTENCE.confirmAccount,
    amPrimary: "Confirm the account",
    ownerPrimary: "Confirm the account",
    mode: "confirm",
    hint: ACCOUNT_HINT,
    detail:
      "This would go to an owner, loan or company-to-company account, which needs a person to confirm.",
  },
  currency_not_usd: blockedPlan(
    "This is not in US dollars.",
    ASK_OWNER_SENTENCE,
    OWNER_SETUP,
    ASK_OWNER_LABEL,
    OWNER_SETUP,
    "This is not in US dollars.",
  ),
  // Round-1 review, MUST-FIX 1. The wallet list is part of the company's books
  // (`company.yaml` `moov_wallets`) and of the Worker's `MOOV_WALLETS`
  // variable; sync fails closed while the two disagree. There is no console
  // page for it, so the card says where the change happens and that it is not
  // here, and invents no control.
  moov_wallet_config_drift: blockedPlan(
    "The wallet list in the books does not match the wallets the processor reports.",
    LEDGER_MAP_BANK,
    LEDGER_MAP_BANK,
    "Nothing to do yet",
    "Nothing to do yet",
    "The wallet list in the books does not match the wallets the processor reports.",
  ),
  return_unshaped: blockedPlan(
    "This payment came back in a shape we cannot book.",
    ASK_OWNER_SENTENCE,
    "Review the returned payment.",
    ASK_OWNER_LABEL,
    "Review the returned payment.",
    "This returned payment cannot be booked as it is.",
  ),
  unexplained: blockedPlan(
    "This wallet movement needs a look.",
    ASK_OWNER_SENTENCE,
    "Review this wallet movement.",
    ASK_OWNER_LABEL,
    "Review this wallet movement",
    "This wallet movement needs a person.",
  ),
  breakdown_mixed_kinds: {
    ...nonePlan(
      "This payment mixes a deposit with rent or fees, so it was not booked.",
      "The tenant portal has to send the deposit and the rent as separate payments.",
      "A tenant payment mixes amounts that cannot be booked together",
    ),
    // rd16 U, C16: the AM's next step. "Ask the owner." sent them to a person
    // who cannot clear it either — the split has to come from the tenant
    // portal — so the card says that there is nothing for the AM to do and what
    // actually has to happen (the pattern `TRUST_DEPOSIT_AM` already uses).
    amAction:
      "Nothing to do yet. The tenant portal has to send the deposit and the rent as separate payments.",
  },
  // pipeline/moov_wallet.py `_tenant_split_entry` (~1822-1823): a deposit in a
  // property company's wallet can never book there. The deposit that books is
  // the money once it is in the trust wallet — a NEW movement, and nothing
  // here can move money (Law 2) — so the card names the move, exactly like its
  // mirror `breakdown_income_in_trust_wallet` (round-4 review, finding 3).
  breakdown_deposit_not_in_trust_wallet: {
    happened:
      "This deposit arrived in a property wallet. Deposits belong in the trust company.",
    amAction: TRUST_DEPOSIT_AM,
    ownerAction:
      "This deposit has to be moved into the trust wallet; nothing here can move it.",
    amPrimary: ASK_OWNER_LABEL,
    ownerPrimary: "The deposit has to be moved by hand",
    mode: "none",
    hint: "Nothing here can move money.",
    title: "A security deposit arrived in the wrong wallet",
  },
  // pipeline/moov_wallet.py `_tenant_split_entry`: rent, fees or money paid
  // ahead in a trust company's wallet can never book there. Books cannot move
  // money (Law 2), so a person has to move it and the card names that.
  breakdown_income_in_trust_wallet: {
    happened: "Rent or fees arrived in the trust wallet. They belong in the property company.",
    amAction: TRUST_MONEY_AM,
    ownerAction:
      "This money has to be moved out of the trust wallet; nothing here can move it.",
    amPrimary: ASK_OWNER_LABEL,
    ownerPrimary: "The money has to be moved by hand",
    mode: "none",
    hint: "Nothing here can move money.",
    title: "Rent arrived in the trust wallet",
  },
  // Round-4 review, finding 2: the stored breakdown is frozen at the Worker's
  // first validated read (`storeTenantBreakdown`, ON CONFLICT DO NOTHING) and
  // the gross is fixed, so the amounts can never add up for THIS movement; the
  // corrected amounts come on a new payment — the same truth as
  // `breakdown_mixed_kinds`, and the same treatment (no exit line).
  breakdown_sum_mismatch: nonePlan(
    "The amounts listed do not add up to the payment.",
    "The tenant portal has to send the corrected amounts on a new payment.",
    "A tenant payment's amounts do not add up",
  ),
  breakdown_unreadable: nonePlan(
    "The amounts for this payment could not be read.",
    "The tenant portal has to send this payment again.",
    "A tenant payment could not be read",
  ),
  // pipeline/moov_wallet.py `_tenant_split_entry`: the only producer is
  // `schema_version != TENANT_BREAKDOWN_VERSION`, so re-sending the same
  // payload re-cards it until the books learn the format. The fix is on the
  // books side, and the card says so instead of sending anyone on an errand.
  breakdown_version_unknown: {
    happened: "This payment arrived in a format the books do not know yet.",
    amAction: FORMAT_AM,
    ownerAction:
      "Books has to support this payment's format; sending it again will not help.",
    amPrimary: ASK_OWNER_LABEL,
    ownerPrimary: "The books have to support this format",
    mode: "none",
    hint: "Sending the same payment again will not help.",
    title: "A tenant payment arrived in an unknown format",
  },
  breakdown_lease_number_invalid: nonePlan(
    "The lease number on this payment is missing or not usable.",
    "The tenant portal has to send it with a usable lease number.",
    "A tenant payment is missing a usable lease number",
  ),
  breakdown_account_not_in_chart: nonePlan(
    "An account this payment needs is not open in the books yet.",
    "Open the account in the books.",
    "An account for this payment is not open yet",
  ),
  breakdown_period_locked: closedMonthPlan(),
  // pipeline/moov_wallet.py `_reversed_after_booking`: "Booking a reversing
  // entry is a post-go-live owner decision and is not done here; this only
  // makes the problem visible." So the card says a person decides, and it
  // must not claim the payment books itself.
  breakdown_booked_then_reversed: {
    happened:
      "This payment was booked, then the payment processor showed it returned. Nothing was changed in the books.",
    amAction: CORRECT_IT_AM,
    ownerAction: "Correcting it is your decision; nothing changes automatically.",
    amPrimary: ASK_OWNER_LABEL,
    ownerPrimary: "Correcting it is your decision",
    mode: "none",
    hint: "Nothing here can correct it.",
    title: "A booked payment was returned",
  },
  // pipeline/moov_wallet.py `_tenant_split_entry` (~1830-1832): the check
  // tests the DERIVED location for presence only, and the Worker derives it on
  // every read (`tenantLocationFor`/`trustLocationFor`: null when the books do
  // not own the property, never a guess). An absent, empty or malformed
  // property is refused upstream as `breakdown_refused_property_missing` /
  // `_malformed`, so a card of this family always names a property — the
  // portal DID send one. The stored breakdown never changes (the first
  // validated read stands), so a re-send changes nothing; the fix is the
  // books owning that property, and once they do the same movement books on
  // the next run (round-4 review, finding 1).
  property_not_owned: {
    happened: causeSentence("property_not_owned"),
    // rd16 U, C16: the AM read where it has to be fixed but not whether they
    // had anything to do. Say both, in the order the other setup cards use.
    amAction: `Nothing to do yet. ${LEDGER_ADD_PLACE} Sending it again will not help.`,
    ownerAction: `${LEDGER_ADD_PLACE} Sending it again will not help.`,
    amPrimary: "Nothing to do yet",
    ownerPrimary: "Nothing to do yet",
    mode: "none",
    hint: "Sending the same payment again will not help.",
    title: "This payment names a property this company does not own",
  },
  // Adding a location is an action nobody can do in the console (decision 2),
  // so both roles read that sentence through the capability's advice; the old
  // "Set up the property as a location." named no place to do it, and the AM
  // was sent to ask an owner who has no control for it either (rd r12 F1, R9).
  property_not_configured: {
    happened: "The property on this payment is not set up as a location yet.",
    amAction: LEDGER_ADD_PLACE,
    ownerAction: LEDGER_ADD_PLACE,
    amPrimary: "Nothing to do yet",
    ownerPrimary: "Nothing to do yet",
    mode: "none",
    hint: NO_SINGLE_ACCOUNT,
    title: "This property is not a location yet",
  },
  // pipeline/moov_wallet.py: the split is decided again and the write retried
  // on every run, so nobody has to act for it to be tried again. A `wait`
  // plan: the chip says the system is trying, not that a person must.
  tenant_split_booking_failed: {
    happened:
      "Booking this payment failed, so it was not booked. Books tries again on the next run.",
    amAction: FINALLY_LOOK_AM,
    ownerAction: "Nothing to do yet. If it keeps failing, it needs a closer look.",
    amPrimary: "Nothing to do yet",
    ownerPrimary: "Nothing to do yet",
    mode: "wait",
    hint: NOTHING_YET,
    title: "Booking this tenant payment failed",
  },
  breakdown_refused: refusedPlan(
    "The payment labels were refused, so it was not booked.",
  ),
  breakdown_refused_location_not_chosen: refusedPlan(
    "The payment labels were refused, and no location was chosen.",
  ),
  breakdown_refused_account_not_in_chart: refusedPlan(
    "The payment labels were refused, and the account is not open.",
  ),
  breakdown_refused_period_locked: closedMonthPlan(),
  breakdown_refused_approval_booking_failed: refusedPlan(
    "The payment labels were refused, and booking it failed.",
  ),
};

export const MOOV_CARD_REASON_CODES: readonly string[] = Object.keys(MOOV_PLANS).sort();

/**
 * A Moov reason whose fix is outside this card: the month is closed, the
 * account is not open, a setting is wrong, or the payment shape cannot be
 * booked. No account choice can make these work, so neither the card nor a
 * bulk Book may offer an approve action for them.
 *
 * `wait` reasons end by themselves; `refused` reasons are answered with a
 * category and note; `account`/`confirm`/`location`/`choose` are actionable.
 */
export function isBlockedMoovReason(code: string): boolean {
  const cleaned = code.trim();
  // A refused payment is answered with a category and a note, except when its
  // month is closed: nothing the person writes can book it.
  if (cleaned.startsWith("breakdown_refused") && cleaned !== "breakdown_refused_period_locked") {
    return false;
  }
  const plan = (MOOV_PLANS as Readonly<Record<string, MoovPlan | undefined>>)[cleaned];
  if (plan !== undefined) return plan.mode === "blocked" || plan.mode === "none";
  // Unknown shapes fail closed: the card says "This needs a look" and the
  // presenter does not know a bookable action for it.
  return true;
}

/**
 * A Moov reason that ends by itself: the fee has not posted, the transfer has
 * not finished or failed, or the date is missing. The presenter says "There is
 * nothing to approve yet", and the pipeline never reads an approval for these
 * hub-payout cards, so an approve would only produce a false "Saved" and the
 * card coming back. Neither the card nor a bulk Book may offer one.
 */
export function isWaitingMoovReason(code: string): boolean {
  const plan = (MOOV_PLANS as Readonly<Record<string, MoovPlan | undefined>>)[code.trim()];
  return plan !== undefined && plan.mode === "wait";
}

/**
 * The single question the card and the bulk Book both ask: must the approve
 * action be withheld for this reason? (Blocked/none/unknown, or waiting.)
 */
export function isMoovApproveWithheld(code: string): boolean {
  return isBlockedMoovReason(code) || isWaitingMoovReason(code);
}


const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

const LONG_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

const UUID_RE =
  /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;

interface MoneyParts {
  cents: bigint | null;
  direction: "in" | "out" | "none";
  label: string | null;
}

interface Draft {
  title: string;
  happened: string;
  suggest: string;
  primaryLabel: string;
  secondary: readonly string[];
  chip: CardChip;
  family: string;
  groupKey: string;
  disabledHint: string;
  details: readonly string[];
}

function stripTags(value: string): string {
  const withoutBlocks = value
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, "");
  let out = "";
  let skipping = false;
  for (const char of withoutBlocks) {
    if (char === "<") {
      skipping = true;
      continue;
    }
    if (char === ">" && skipping) {
      skipping = false;
      continue;
    }
    if (!skipping) out += char;
  }
  return out;
}

function visibleBank(value: string | undefined): string {
  return amVisibleDisplayText(stripTags(value ?? "")).replaceAll("\uFFFD", "");
}

function scrubFace(value: string, hidden: readonly string[]): string {
  let text = amVisibleDisplayText(stripTags(value));
  text = text.replace(UUID_RE, "");
  for (const secret of hidden) {
    if (secret.length >= 8) text = text.split(secret).join("");
  }
  text = text.replaceAll("_", " ");
  text = text.replaceAll("CoA", "");
  text = text.replaceAll("[object Object]", "");
  text = text.replaceAll("[object", "");
  return text.replace(/[ ]{2,}/g, " ").trim();
}

function scrubLoose(value: string): string {
  return amVisibleDisplayText(stripTags(value)).replace(/[ ]{2,}/g, " ").trim();
}

function clipWords(text: string, max: number): string {
  const words = text.split(" ").filter((word) => word.length > 0);
  if (words.length <= max) return text;
  return words.slice(0, max).join(" ");
}

/** Queue reasons whose card carries one bank transaction's own amount. */
const BANK_AMOUNT_REASONS: ReadonlySet<string> = new Set([
  "new-vendor",
  "low-confidence",
  "anomaly",
  "possible-personal",
  "unmatched-deposit",
  "booking-failed",
  // Both carry the bank transaction's own amount (round 10, A9-M1): a
  // receipt not attached to its property and an untracked counterparty
  // transfer. Leaving them out showed money out as positive while Activity
  // and Books showed the same line negative.
  "receipt_card_not_attached",
  "untracked-counterparty",
]);

/**
 * THE one place a queue card's amount changes sign for display (round eight
 * A-M2). The bank feed counts money OUT as positive, and the Worker returns it
 * as stored; the console shows money out as negative and money in as positive
 * (Activity, Books, wallet cards). A card that carries a bank transaction's own
 * amount is negated here, for display and for the totals built from it, and
 * NOTHING else changes: what is posted or sent never goes through this
 * function. A wallet (Moov) card already uses the console's sign, so it is left
 * alone, as are blocker cards and anything that is not one bank transaction.
 * An amount that is missing, zero or not exact is returned as it was.
 *
 * The bank feed's sign was read from the Worker and pipeline source, not from
 * live data. If it turns out the other way round, reverse it HERE.
 */
export function queueAmountDecimal(item: QueueItem): string | undefined {
  const raw = item.amount;
  if (raw === undefined) return raw;
  const bank =
    (item.plaidTransactionId ?? "").length > 0 && BANK_AMOUNT_REASONS.has(item.reason) && !isMoov(item);
  if (!bank) return raw;
  const cents = decimalToCents(raw.trim());
  if (cents === null || cents === 0n) return raw;
  const flipped = -cents;
  const abs = flipped < 0n ? -flipped : flipped;
  const whole = (abs / 100n).toString();
  const frac = (abs % 100n).toString().padStart(2, "0");
  return `${flipped < 0n ? "-" : ""}${whole}.${frac}`;
}

function moneyParts(amount: string | undefined): MoneyParts {
  if (amount === undefined || amount.trim().length === 0) {
    return { cents: null, direction: "none", label: null };
  }
  const trimmed = amount.trim();
  if (/^-?0+(?:\.0+)?$/.test(trimmed)) {
    return { cents: 0n, direction: "none", label: null };
  }
  const cents = decimalToCents(trimmed);
  if (cents === null || cents === 0n) {
    return { cents: cents === 0n ? 0n : null, direction: "none", label: null };
  }
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  return {
    cents: abs,
    direction: negative ? "out" : "in",
    label: formatCents(abs),
  };
}

function formatShortDate(iso: string | undefined): string | null {
  if (iso === undefined || iso.startsWith("1970-01-01")) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match === null) return null;
  const month = Number(match[2]);
  const day = Number(match[3]);
  const label = SHORT_MONTHS[month - 1];
  if (label === undefined || !Number.isInteger(day) || day < 1 || day > 31) return null;
  return `${label} ${String(day)}`;
}

function monthInSubject(subject: string): string | null {
  for (const month of LONG_MONTHS) {
    if (subject.includes(`${month} `) || subject.endsWith(month)) return month;
  }
  return null;
}

function moovType(descriptor: string | undefined): MoovTypeInfo {
  const cleaned = visibleBank(descriptor).toLowerCase().replace(/\s+/g, " ").trim();
  const raw = cleaned.startsWith("moov ") ? cleaned.slice(5).trim() : cleaned;
  const token = raw.split(" ")[0] ?? "";
  if (token === "payout") return PAYOUT;
  if (token === "top-up" || token === "topup") return TOP_UP;
  if (token === "cash-out" || token === "cashout") return CASH_OUT;
  if (token === "moov-fee") return MOOV_FEE;
  return WALLET_MOVEMENT;
}

/**
 * The last words of an account path, or null when there is nothing to read.
 * The ONE leaf rule (`accountLeafLabel`): it also words a between-companies
 * account as "Due from <company>" instead of printing the counterparty's
 * internal key, which a hand split on ":" cannot do (rd13 L1).
 */
function accountLeaf(
  account: string | undefined,
  companies: AccountLabelContext,
): string | null {
  const cleaned = visibleBank(account).trim();
  if (cleaned.length === 0) return null;
  const leaf = accountLeafLabel(cleaned, companies);
  return leaf === ACCOUNT_UNKNOWN_TEXT ? null : leaf;
}

/**
 * A readable, unambiguous account path. The first segment is the company root
 * (`GVPM:Expenses:Rent`) and is dropped when a real path remains; the tail is
 * shown with ` › ` so two same-leaf accounts cannot look identical.
 * The stored/submitted value stays the full colon name. The words come from
 * `accountLabel` (rd Y0) so every surface writes an account the same way.
 */
function plainAccountWords(
  account: string | undefined,
  companies: AccountLabelContext,
): string | null {
  const cleaned = visibleBank(account).trim();
  if (cleaned.length === 0) return null;
  const label = accountLabel(cleaned, companies);
  return label === ACCOUNT_UNKNOWN_TEXT ? null : label;
}

function forRole(role: StaffRole, am: string, owner: string): string {
  return role === "owner" ? owner : am;
}

/**
 * Owner item 4 (2026-10-26). Two blocker exit sentences in the shared
 * declaration end by telling the reader to "Escalate to owner". An owner
 * reading the card must not be told to escalate to themselves, and an account
 * manager is asking the owner. Rewrite ONLY that clause; every other sentence
 * and every machine field (exitKind, clearMode, ids) stays as declared.
 *
 * The hard-match-refuse row says "Escalate with a note starting with
 * keep-bank:" and does not match this clause, so the owner keeps that
 * instruction.
 */
const ESCALATE_TO_OWNER_CLAUSE = /(^| )Escalate to owner( if [^.]*)?\./g;

export function roleAwareEscalateClause(text: string, role: StaffRole): string {
  return text.replace(
    ESCALATE_TO_OWNER_CLAUSE,
    (_whole: string, lead: string, condition: string | undefined) => {
      if (role === "owner") return "";
      return `${lead}Ask the owner${condition ?? ""}.`;
    },
  );
}

function secondaryFor(role: StaffRole, withAsk: boolean): readonly string[] {
  if (role === "owner") {
    return withAsk ? ["Skip for now", "Dismiss"] : ["Skip for now"];
  }
  return withAsk ? [ASK_OWNER_LABEL, "Skip for now"] : ["Skip for now"];
}

// Chip words are sentence case, the same as "Setup fix" and "Month closed"
// (round-2 review, note (d)). The tone strings stay lowercase: they are
// selectors, not words anyone reads.
function needsYou(): CardChip {
  return { text: "Needs you", tone: "needs you" };
}

function waitingSystem(): CardChip {
  return { text: "Waiting on the system", tone: "waiting on the system" };
}

function waitingOwner(role: StaffRole): CardChip {
  if (role === "owner") {
    return { text: OWNER_CHIP, tone: "waiting on the owner" };
  }
  return { text: "Waiting on the owner", tone: "waiting on the owner" };
}

/**
 * The chip for a card that cannot be answered in the console and is nobody's
 * errand: the decided "Setup fix" word, for both roles (R4-26).
 */
function setupFixChip(): CardChip {
  return { text: SETUP_FIX_CHIP, tone: "waiting on the owner" };
}

function chipFor(mode: MoovMode, role: StaffRole): CardChip {
  if (mode === "wait") return waitingSystem();
  if (mode === "blocked" || mode === "none") return waitingOwner(role);
  return needsYou();
}

/**
 * Wallet reasons whose card asks a person to look at the movement itself, or
 * to decide what happens to it: a returned shape, an anomaly, a booked payment
 * Moov now shows returned, an unexplained movement, a Moov fee that failed (it
 * can never post, so waiting for it is a lie — round 5), and amounts that can
 * never agree (round 6: both rows completed, the amounts frozen). There is
 * nothing to configure and nothing to pick, so none of them is a setup fix
 * even though Approve is withheld for them (round-1 review SHOULD-FIX 2;
 * round 2 narrowed `tenant_split_booking_failed` out of this set, because the
 * pipeline retries it by itself). They are counted under "Needs you".
 */
const MOOV_NEEDS_LOOK_CODES: ReadonlySet<string> = new Set([
  "amount_disagrees",
  "anomaly",
  "breakdown_booked_then_reversed",
  "fee_not_success",
  "return_unshaped",
  "unexplained",
]);

function chipForCode(mode: MoovMode, role: StaffRole, code: string): CardChip {
  if (MOOV_NEEDS_LOOK_CODES.has(code)) return needsYou();
  return chipFor(mode, role);
}

function asksOwner(mode: MoovMode): boolean {
  return (
    mode === "account" ||
    mode === "choose" ||
    mode === "confirm" ||
    mode === "location" ||
    mode === "refused"
  );
}

function hiddenIds(item: QueueItem): string[] {
  const ids: string[] = [];
  if (item.plaidTransactionId !== undefined && item.plaidTransactionId.length >= 8) {
    ids.push(item.plaidTransactionId);
  }
  const subject = item.subject ?? "";
  if (subject.startsWith(MOOV_CARD_SUBJECT_PREFIX)) {
    const id = subject.slice(MOOV_CARD_SUBJECT_PREFIX.length).trim();
    if (id.length >= 8) ids.push(id);
  }
  return ids;
}

function reasonCode(body: string | undefined): string {
  return visibleBank(body).trim();
}

function moovId(item: QueueItem): string | null {
  const subject = item.subject ?? "";
  if (!subject.startsWith(MOOV_CARD_SUBJECT_PREFIX)) return null;
  const id = subject.slice(MOOV_CARD_SUBJECT_PREFIX.length).trim();
  return id.length > 0 ? id : null;
}

/**
 * Q-d: the amount is deliberately NOT part of the key. Q-c included it, which
 * turned 31 different-sized wallet payments into 31 one-card groups. The
 * group header shows the count and the total instead.
 */
function moovGroup(company: CompanySlug, type: MoovTypeInfo, money: MoneyParts): string {
  return slugKey(company, `moov|${type.key}|${money.direction}`);
}

function bankToken(item: QueueItem): string {
  const descriptor = visibleBank(item.descriptor);
  const merchant = visibleBank(item.merchant);
  const candidates = matchTextCandidates(descriptor, merchant);
  let best = "";
  for (const candidate of candidates) {
    if (candidate.length > best.length) best = candidate;
  }
  const raw = best.length > 0 ? best : merchant.length > 0 ? merchant : descriptor;
  const token = raw.trim().toLowerCase().replace(/\s+/g, " ");
  return token.length > 0 ? token : "unknown";
}

function groupFor(item: QueueItem, family: string, money: MoneyParts): string {
  return slugKey(item.company, `${family}|${bankToken(item)}|${money.direction}`);
}

function noRuleHappened(type: MoovTypeInfo, date: string | null): string {
  const when = date === null ? "" : ` on ${date}`;
  return `A ${type.noun}${when} has not been booked. No rule covers ${type.plural} yet.`;
}

function moovDefaultTitle(money: MoneyParts, companyWords: string): string {
  // `face.company` returns "this company" when no display name is known;
  // "the this company wallet" is not English, so the possessive form is used.
  const wallet =
    companyWords === COMPANY_UNKNOWN_TEXT ? "this company's" : `the ${companyWords}`;
  if (money.label !== null && money.direction === "out") {
    return `${money.label} left ${wallet} wallet`;
  }
  if (money.label !== null && money.direction === "in") {
    return `${money.label} arrived in ${wallet} wallet`;
  }
  if (money.label !== null) return `${money.label} moved in ${wallet} wallet`;
  return `A wallet movement on ${wallet} wallet`;
}

function moovTitle(
  plan: MoovPlan,
  money: MoneyParts,
  companyWords: string,
): string {
  return plan.title ?? moovDefaultTitle(money, companyWords);
}

function moovPrimary(plan: MoovPlan, role: StaffRole, account: string | null): string {
  if (plan.mode === "account" && account !== null) return `Book to ${account}`;
  if (plan.mode === "account") return plan.idlePrimary ?? plan.amPrimary;
  return forRole(role, plan.amPrimary, plan.ownerPrimary);
}

function moovSuggest(
  plan: MoovPlan,
  role: StaffRole,
  usually: string,
): string {
  if (plan.mode === "refused") return "Choose the category and write what you found.";
  const action = forRole(role, plan.amAction, plan.ownerAction);
  // Nothing is chosen on a waiting, blocked or unbookable card, so "usually
  // goes to..." would only mislead: the plan's own next step stands alone.
  if (plan.mode === "wait" || plan.mode === "blocked" || plan.mode === "none") {
    return action;
  }
  return usually === "" ? action : `${usually} ${action}`;
}

function moovHint(plan: MoovPlan, leaf: string | null): string {
  if ((plan.mode === "account" || plan.mode === "confirm") && leaf !== null) return "";
  return plan.hint;
}

function commonDetails(item: QueueItem): string[] {
  const details: string[] = [];
  if (typeof item.confidence === "number" && Number.isFinite(item.confidence)) {
    details.push(`Confidence: ${String(Math.round(item.confidence * 100))}%`);
  }
  const why = evidenceQueueWhy(item.aiRationale);
  if (why !== null) details.push(evidenceSentence(why));
  const cause = bookingFailureCause(item.aiRationale);
  if (cause !== null && cause.length > 0) details.push(cause);
  const descriptor = visibleBank(item.descriptor).trim();
  if (descriptor.length > 0) details.push(`Descriptor: ${descriptor}`);
  const merchant = visibleBank(item.merchant).trim();
  if (merchant.length > 0) details.push(`Merchant: ${merchant}`);
  if (item.plaidTransactionId !== undefined && item.plaidTransactionId.length > 0) {
    details.push(`Transaction: ${item.plaidTransactionId}`);
  }
  return details;
}

function evidenceSentence(why: string): string {
  switch (why) {
    case "no_corroboration":
      return BANK_TEXT_NOT_ENOUGH_COPY;
    case "judgment_equity_needs_character":
    case "judgment_intercompany_needs_character":
    case "judgment_loan_needs_character":
      return "This account needs evidence of what kind of transaction this is.";
    case "weak_below_confidence":
      return "There is some supporting detail, but not enough to book this ourselves.";
    case "evidence_gate_off_low_confidence":
      return "This is not sure enough to book on its own.";
    default:
      if (why.startsWith("judgment_") && why.endsWith("_needs_character")) {
        return "This account needs evidence of what kind of transaction this is.";
      }
      return "This needs more support before it can be booked.";
  }
}

function isMoov(item: QueueItem): boolean {
  return (
    item.reason === "close-blocked" &&
    (item.subject ?? "").startsWith(MOOV_CARD_SUBJECT_PREFIX)
  );
}

function isArf1(text: string | undefined): boolean {
  return bookingFailureCause(text) !== null;
}

function isRecovery(item: QueueItem): boolean {
  if (item.reason === "unmatched-deposit") return false;
  return item.reason === "booking-failed" || isArf1(item.aiRationale);
}

function isRemoval(item: QueueItem): boolean {
  return item.eventType === "removed";
}

function presentMoov(item: QueueItem, ctx: CardPresenterContext): Draft {
  const code = reasonCode(item.body);
  const type = moovType(item.descriptor);
  const money = moneyParts(item.amount);
  const date = formatShortDate(item.txnDate ?? item.dueDate);
  const company = face.company(item.company, presenterDirectory(ctx), "sentence");
  const resolved = item.resolvedAccount?.trim() ?? "";
  const sharedWords = resolved === "" ? null : (ctx.accountWords?.(item.company, resolved) ?? null);
  const account =
    sharedWords ?? plainAccountWords(item.resolvedAccount, presenterLabelContext(ctx));
  const leaf = accountLeaf(item.resolvedAccount, presenterLabelContext(ctx));
  const plan = MOOV_PLANS[code];
  const tx = moovId(item);
  const details = [`Reason: ${code.length > 0 ? code : "missing"}`, ...commonDetails(item)];
  if (tx !== null) details.push(`Transaction: ${tx}`);
  const full = MOOV_BREAKDOWN_REASON_COPY[code];
  if (full !== undefined) details.push(full);
  if (plan === undefined) {
    return unknownMoov(code, ctx.role, type, company, money, details, item);
  }
  if (plan.detail !== undefined) details.push(plan.detail);
  const happened =
    code === "no_rule"
      ? noRuleHappened(type, date)
      : plan.happened;
  const closedMonth = MOOV_CLOSED_MONTH_CODES.has(code);
  // The "where payouts usually go" hint is for the card that asks which
  // account a payout belongs to, and nowhere else: on a card whose own
  // sentence already names the account problem (a bill pointing at the
  // company's own bank account, an unreadable bill) it only repeats it
  // (round-2 review, note (b)).
  const usuallyText = code === "no_rule" ? type.usually : "";
  return {
    title: moovTitle(plan, money, company),
    happened: closedMonth ? closedMonthSentence(item, ctx.role) : happened,
    suggest: closedMonth
      ? CLOSED_MONTH_ACTION
      : moovSuggest(plan, ctx.role, usuallyText),
    primaryLabel: moovPrimary(plan, ctx.role, account),
    secondary: secondaryFor(ctx.role, asksOwner(plan.mode)),
    chip: chipForCode(plan.mode, ctx.role, code),
    family: "moov",
    groupKey: moovGroup(item.company, type, money),
    disabledHint: closedMonth ? "" : moovHint(plan, leaf),
    details,
  };
}

/**
 * Round 5 (MUST-FIX 2): the reasons with a plain sentence but no plan used to
 * fall to one generic card ("This needs a look" / "needs a person to look at
 * it" / "Needs you"), whose face said the OPPOSITE of the card's own exit
 * line — a system wait was called a person's errand, books-side setup fixes
 * were called a look, and the closed month lost its own word. Every planless
 * reason is now CLASSIFIED, one entry with its one-line proof, and the face
 * is built from that class; the sweep fails when a wallet reason has no entry
 * (a thirty-first cannot fall to the generic card silently). A reason in NO
 * table at all (genuinely unknown) keeps the fail-closed generic card.
 */
type PlanlessState = "wait" | "setup" | "month" | "decide";
/**
 * `happened` is the FACE sentence (≤ 20 words — `cardPresentation` clips the
 * face line at 20); the reason's fuller sentence in `MOOV_BREAKDOWN_REASON_COPY`
 * stays behind Show details, the same split the planned cards have.
 */
export const MOOV_PLANLESS_CLASS: Readonly<
  Record<string, { state: PlanlessState; happened: string; proof: string }>
> = {
  // -- wait: the pipeline re-reads or re-writes on every run; nobody acts. --
  fee_row_missing: {
    state: "wait",
    happened:
      "The processor fee for this trust deposit has not settled yet; when it settles, the deposit books itself.",
    proof: "moov_wallet.py 1849-1850 re-checks the settled fee row on every run; 3179-3186 holds the deposit while it waits; a FAILED fee never settles, so the claim is conditional and the unconditional promise is gone (round 5)",
  },
  fee_topup_booking_failed: {
    state: "wait",
    happened: causeSentence("fee_topup_booking_failed"),
    proof: "moov_wallet.py 2291-2295 cards the write failure; the movement is re-read and re-written on every run",
  },
  fee_topup_missing: {
    state: "wait",
    happened:
      "The processor fee on this trust deposit is still owed; the top-up that repays it has not arrived.",
    proof: "moov_wallet.py 2505-2513 re-emits the card while the receivable is open; the top-up that arrives books itself (2419-2438)",
  },
  topup_duplicate: {
    state: "wait",
    happened: "An earlier top-up for this deposit keeps the fee while it is live.",
    proof: "moov_trust_fee_topup.ts 448-490 (V5, M2 484-487): the earliest-read twin keeps the deposit while it is live and the verdict is re-derived on every read, so this top-up books if the twin is refused",
  },
  // -- setup: the movement books once a cause outside this card is fixed. Most
  //    causes are books-side (an account, a location); three are GV's own
  //    lists (the trust map, the hub property lists), where no books file
  //    decides — those say so in their own next step
  //    (`MOOV_PLANLESS_GV_NEXT_STEP`, round 6). --
  fee_topup_account_not_in_chart: {
    state: "setup",
    happened: "An account this fee top-up needs is not open in the books yet.",
    proof: "moov_wallet.py 2350-2352, 2416-2418: the write passes once the account is open in the books",
  },
  fee_topup_location_unknown: {
    state: "setup",
    happened: causeSentence("fee_topup_location_unknown"),
    proof: "moov_wallet.py 2334-2338: the location is matched from the books' own data on every read",
  },
  topup_company_not_served: {
    state: "setup",
    happened: causeSentence("topup_company_not_served"),
    proof: "TENANT_TRUST_COMPANIES is a frozen code constant (ingest-worker/src/moov_tenant_breakdown.ts 78, pipeline/moov_wallet.py 170-176), re-read at V3 (moov_trust_fee_topup.ts 383-384) and again in the pipeline (moov_wallet.py 2383-2388): not a books file, so a GV change and a deploy fix it, not company.yaml; the same top-up books once the list carries the company",
  },
  topup_property_not_owned: {
    state: "setup",
    happened: causeSentence("topup_property_not_owned"),
    proof: "tenantLocationFor reads RECEIPT_HUB_BY_COMPANY (receipt_hub_config.ts 405-409), a Worker code constant mirrored into company.yaml; V3 re-checks it on every read (moov_trust_fee_topup.ts 386-387): not a books file, so a GV change adds the property and the same top-up books",
  },
  breakdown_operating_company_unknown: {
    state: "setup",
    happened:
      "Books could not tell which property company owns this deposit's property, so it was not booked.",
    proof: "trustOperatingCompanyFor walks the trust's served companies' code-resident hub properties (moov_tenant_breakdown.ts 376-383) and the pipeline re-checks the code map (moov_wallet.py 1844-1856): not a books file, so a GV change makes the same deposit book",
  },
  // -- month: a closed month holds it; its own word and its own sentence. --
  fee_topup_period_locked: {
    state: "month",
    happened: "",
    proof: "moov_wallet.py 2288: the write hits PeriodLockedError; once the month is reopened the same top-up books",
  },
  // -- decide: the money or the shape is dead for good and a person decides;
  //    the real control on the card is close-with-a-note. --
  fee_topup_amount_mismatch: {
    state: "decide",
    happened: FEE_TOPUP_AMOUNT_MISMATCH_COPY,
    proof: "moov_wallet.py 2322-2324, 2413-2415: both amounts are fixed for this movement; the replay path books a top-up the books already expect (round-2 review, MUST-FIX 1)",
  },
  fee_topup_duplicate: {
    state: "decide",
    happened:
      "A second top-up arrived for a deposit whose fee was already repaid; the account manager recovers it.",
    proof: "moov_wallet.py 2344-2348 (M2): the payer's ledger already expensed this deposit's fee, and a deposit's fee is paid once",
  },
  fee_topup_without_receivable: {
    state: "decide",
    happened: "A fee top-up arrived for a deposit that has no fee owed, so nothing was booked.",
    proof: "moov_wallet.py 2392-2394: the deposit has no fee owed (booked before the switch)",
  },
  fee_topup_receivable_closed: {
    state: "decide",
    happened:
      "A fee top-up arrived for a fee already written off; the account manager decides what happens to the money.",
    proof: "moov_wallet.py 2405-2411: the receivable is already cleared or written off",
  },
  fee_topup_unreadable: {
    state: "decide",
    happened: causeSentence("fee_topup_unreadable"),
    proof: "moov_wallet.py 2309-2313: the Worker stored no readable top-up and the parse refusal is written once (storeTenantBreakdownRefusal, ON CONFLICT DO NOTHING)",
  },
  fee_receivable_deposit_reversed: {
    state: "decide",
    happened:
      "This deposit was returned with its fee still owed; writing the fee off is the account manager's decision.",
    proof: "moov_wallet.py 2494-2504: the deposit was returned, the receivable will never be cleared, and the write-off is the account manager's decision",
  },
  topup_has_fee: {
    state: "decide",
    happened: causeSentence("topup_has_fee"),
    proof: "moov_trust_fee_topup.ts 333-335 (V1): a top-up carrying its own Moov fee row is refused outright",
  },
  topup_amount_differs: {
    state: "decide",
    happened: causeSentence("topup_amount_differs"),
    proof: "moov_trust_fee_topup.ts 379-381: the wallet rows carry the fee or they never agree",
  },
  topup_not_usd: {
    state: "decide",
    happened: causeSentence("topup_not_usd"),
    proof: "moov_trust_fee_topup.ts 350-352: the row's currency is a Moov fact",
  },
  topup_rows_unexpected: {
    state: "decide",
    happened: causeSentence("topup_rows_unexpected"),
    proof: "moov_trust_fee_topup.ts 369-371: the transfer's row shape is a Moov fact",
  },
  topup_wrong_direction: {
    state: "decide",
    happened: causeSentence("topup_wrong_direction"),
    proof: "moov_trust_fee_topup.ts 354-366, 532-533: the rows' directions are Moov facts",
  },
  topup_not_trust_destination: {
    state: "decide",
    happened: causeSentence("topup_not_trust_destination"),
    proof: "moov_trust_fee_topup.ts 357-366: the destination wallet is a Moov fact",
  },
  topup_deposit_not_a_deposit: {
    state: "decide",
    happened: causeSentence("topup_deposit_not_a_deposit"),
    proof: "moov_trust_fee_topup.ts 402-411: the stored breakdown is frozen at the first validated read",
  },
  topup_fee_differs: {
    state: "decide",
    happened: causeSentence("topup_fee_differs"),
    proof: "moov_trust_fee_topup.ts 420-422: the deposit's settled fee row is a Moov fact",
  },
  topup_lease_differs: {
    state: "decide",
    happened: causeSentence("topup_lease_differs"),
    proof: "moov_trust_fee_topup.ts 423-425: the frozen breakdown's lease against the top-up's tags",
  },
  topup_property_differs: {
    state: "decide",
    happened: causeSentence("topup_property_differs"),
    proof: "moov_trust_fee_topup.ts 426-428: the frozen breakdown's property against the top-up's tags",
  },
  topup_transfer_not_completed: {
    state: "decide",
    happened: causeSentence("topup_transfer_not_completed"),
    proof: "moov_trust_fee_topup.ts 339-349: a failed, cancelled or reversed transfer never settles",
  },
  topup_pre_switch: {
    state: "decide",
    happened:
      "A fee top-up names a deposit whose fee was already booked as an expense before the switch.",
    proof: "moov_trust_fee_topup.ts 412-419 (H1): the deposit's fee was expensed before the switch, so no receivable exists to clear",
  },
};

/** The face sentence for a planless reason: its own short sentence, or the
 * generic line for a reason the table does not know. */
function planlessHappened(code: string): string {
  return (
    MOOV_PLANLESS_CLASS[code]?.happened ??
    "This wallet movement needs a person to look at it."
  );
}

/**
 * Round 6 (must-fix 1). The setup class's definition is "a cause outside this
 * card is fixed and the same movement books". For most of its reasons the
 * cause is a books file (`company.yaml` locations / `moov_wallets`, or an
 * account opened in the chart). Three are NOT: the trust map and the hub
 * property lists are frozen code constants the Worker reads directly
 * (`TENANT_TRUST_COMPANIES`, `RECEIPT_HUB_BY_COMPANY`), so no books edit can
 * make those movements book. Each of the three names the change GV really has
 * to make — the chip stays `Setup fix` because the card still cannot be
 * answered in the console, and the bookings claim stays true: the same
 * movement books once GV's list carries it.
 */
const MOOV_PLANLESS_GV_NEXT_STEP: Readonly<Record<string, string>> = {
  topup_company_not_served:
    "GV has to serve that company from the trust company's list; there is no books change to make.",
  topup_property_not_owned:
    "GV has to add that property to the paying company's property list; there is no books change to make.",
  breakdown_operating_company_unknown:
    "GV has to add that property to a company the trust company serves; there is no books change to make.",
};

/**
 * Round 6 (should-fix 1). A `Waiting on the system` chip says the pipeline
 * will try again; it does not say the card ends by itself. Where the reason
 * also has a person case, the next step carries it, so the face agrees with
 * the reason's own sentence behind Show details: a live twin that may book
 * (money then moved twice), a write that may keep failing, a top-up nobody
 * sent. `fee_row_missing` has no person on this card (a FAILED fee raises its
 * own card), so it keeps the plain line.
 */
const MOOV_PLANLESS_WAIT_NEXT_STEP: Readonly<
  Record<string, { am: string; owner: string }>
> = {
  fee_topup_booking_failed: {
    am: "Nothing to do yet. If it keeps failing, it needs a closer look.",
    owner:
      "Nothing to do yet. If it keeps failing, the account manager has to look at it.",
  },
  fee_topup_missing: {
    am: "Nothing to do yet. If it was never sent, the property company has to send the top-up with the payment processor.",
    owner:
      "Nothing to do yet. If it was never sent, the property company has to send the top-up with the payment processor.",
  },
  topup_duplicate: {
    am: "Nothing to do while that top-up is live. If it books, you recover this one.",
    owner:
      "Nothing to do while that top-up is live. If it books, money moved twice and the account manager recovers this one.",
  },
};

function unknownMoov(
  code: string,
  role: StaffRole,
  type: MoovTypeInfo,
  companyWords: string,
  money: MoneyParts,
  details: string[],
  item: QueueItem,
): Draft {
  const refused = code.startsWith("breakdown_refused");
  const cls = MOOV_PLANLESS_CLASS[code];
  if (!refused && cls !== undefined) {
    // The key is the company SLUG (`item.company`), the same key the planned
    // cards use via `moovGroup`, so one company's wallet movements of one type
    // and direction stay in one sub-group (round 6, should-fix 3). The display
    // name is for the TITLE only.
    const groupKey = moovGroup(item.company, type, money);
    const secondary = secondaryFor(role, false);
    if (cls.state === "month") {
      return {
        title: moovDefaultTitle(money, companyWords),
        happened: closedMonthSentence(item, role),
        suggest: CLOSED_MONTH_ACTION,
        primaryLabel: "Nothing to do yet",
        secondary,
        chip: waitingOwner(role),
        family: "moov",
        groupKey,
        disabledHint: "",
        details,
      };
    }
    if (cls.state === "wait") {
      const next = MOOV_PLANLESS_WAIT_NEXT_STEP[code];
      return {
        title: moovDefaultTitle(money, companyWords),
        happened: planlessHappened(code),
        suggest: next === undefined ? "Nothing to do yet." : forRole(role, next.am, next.owner),
        primaryLabel: "Nothing to do yet",
        secondary,
        chip: waitingSystem(),
        family: "moov",
        groupKey,
        disabledHint: NOTHING_YET,
        details,
      };
    }
    if (cls.state === "setup") {
      // `MOOV_PLANLESS_GV_NEXT_STEP` names the three causes no books file
      // decides; the rest are a change in the books.
      const gv = MOOV_PLANLESS_GV_NEXT_STEP[code];
      return {
        title: moovDefaultTitle(money, companyWords),
        happened: planlessHappened(code),
        suggest:
          gv ??
          forRole(role, MAKE_CHANGE_AM, OWNER_SETUP),
        primaryLabel: gv === undefined
          ? forRole(role, ASK_OWNER_LABEL, OWNER_SETUP)
          : "GV has to fix this",
        secondary,
        chip: waitingOwner(role),
        family: "moov",
        groupKey,
        disabledHint: NO_SINGLE_ACCOUNT,
        details,
      };
    }
    return {
      title: "This needs a look",
      happened: planlessHappened(code),
      suggest: forRole(
        role,
        DEAL_WITH_IT_AM,
        "Nothing here can book it. Deal with it outside this card, then close it with a note.",
      ),
      primaryLabel: forRole(role, ASK_OWNER_LABEL, "Deal with it outside this card"),
      secondary,
      chip: needsYou(),
      family: "moov",
      groupKey,
      disabledHint: NO_SINGLE_ACCOUNT,
      details,
    };
  }
  const split =
    code.startsWith("breakdown_") ||
    code.startsWith("property_") ||
    code === "tenant_split_booking_failed";
  const mode: MoovMode = refused ? "refused" : split ? "none" : "blocked";
  const plan = refused
    ? refusedPlan("The payment labels were refused, so it was not booked.")
    : split
      ? nonePlan("This tenant payment could not be booked on its own.", OWNER_SETUP)
      : blockedPlan(
          "This wallet movement needs a person to look at it.",
          ASK_OWNER_SENTENCE,
          "Review this wallet movement.",
          ASK_OWNER_LABEL,
          "Review this wallet movement",
          ACCOUNT_HINT,
        );
  return {
    title: "This needs a look",
    happened: refused || split ? plan.happened : "This wallet movement needs a person to look at it.",
    suggest: moovSuggest(plan, role, type.usually),
    primaryLabel: moovPrimary(plan, role, null),
    secondary: secondaryFor(role, asksOwner(mode)),
    chip: needsYou(),
    family: "moov",
    groupKey: moovGroup(item.company, type, money),
    disabledHint: plan.hint,
    details,
  };
}

/**
 * The subject prefixes the Worker puts on its blocker cards. Exported so a
 * role test can build its subjects from the REAL prefixes rather than from a
 * hand-typed paraphrase (round-2 review, MUST-FIX 3: a test subject that never
 * matched the live "Statement reconciliation blocked — " proved nothing).
 */
export const BLOCKER_PREFIX = {
  monthClose: "Month-close blocked \u2014 ",
  periodReopened: "Period reopened \u2014 re-close required \u2014 ",
  closedWrite: "Closed-period write blocked \u2014 ",
  missingAccount: "Missing Plaid account_id \u2014 ",
  unmapped: "Unmapped Plaid account \u2014 ",
  conflict: "Receipt match conflict \u2014 ",
  hubBatch: "Unmatched hub GL batch \u2014 ",
  hubRefused: "Hub GL push refused \u2014 ",
  hardMatch: "Receipt hard-match refused \u2014 ",
  matchReview: "Receipt match review \u2014 ",
  statement: "Statement reconciliation blocked \u2014 ",
  stale: "Stale published assertion \u2014 ",
  escrow: "Escrow balance \u2014 ",
} as const;

// ---------------------------------------------------------------------------
// Y5: two states a card can be in that no account choice can leave
// ---------------------------------------------------------------------------
//
// "Setup fix": the card cannot be answered in the console. The thing that
// clears it is a change made elsewhere — a month reopened, an account or a
// property opened in the books, a card or wallet setting changed, or the
// practice correcting what it sent. `docs/design/rd-polish-rules.md` §1 fixes
// the chip word; the lane brief fixes the closed-month exception.
//
// Both sets are lists, not a filter over "has no approve control": an anomaly
// card and a returned, unbooked shape also withhold Approve, and neither is a
// setup fix.

/** Moov reasons a closed month holds (Y5 brief item 2: its own chip word). */
const MOOV_CLOSED_MONTH_CODES: ReadonlySet<string> = new Set([
  "period_locked",
  "breakdown_period_locked",
  "breakdown_refused_period_locked",
  "fee_topup_period_locked",
]);

/**
 * Blocker families with no control on the card: the account or property must
 * be set up, the month reopened, or the published statement corrected.
 */
const SETUP_FIX_BLOCKER_PREFIXES: readonly string[] = [
  BLOCKER_PREFIX.closedWrite,
  BLOCKER_PREFIX.unmapped,
  BLOCKER_PREFIX.missingAccount,
  BLOCKER_PREFIX.stale,
];

/** The month a closed-month card is about, as a person reads it. */
export function closedMonthName(item: QueueItem): string | null {
  // A close-blocked subject carries the period itself ("July 2026", or an ISO
  // period); that is the month the card is about, whatever day the row has.
  const subject = item.subject ?? "";
  const named =
    /(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})/.exec(
      subject,
    );
  if (named !== null) return `${named[1] ?? ""} ${named[2] ?? ""}`.trim();
  const iso = /\b(20\d{2})-(0[1-9]|1[0-2])\b/.exec(subject);
  if (iso !== null) {
    const label = monthText(`${iso[1] ?? ""}-${iso[2] ?? ""}`);
    if (label !== "") return label;
  }
  const fromDate = monthText(item.txnDate ?? item.dueDate);
  if (fromDate !== "") return fromDate;
  return null;
}

/** A card a closed month is holding: it cannot be booked until a reopen. */
export function isClosedMonthCard(item: QueueItem): boolean {
  if (isMoov(item)) return MOOV_CLOSED_MONTH_CODES.has((item.body ?? "").trim());
  if (resolveHoldCode(item) === "bill_period_locked") return true;
  return (item.subject ?? "").startsWith(BLOCKER_PREFIX.closedWrite);
}

/**
 * One sentence, once, for both roles: the month that is closed. The next step
 * (decision 2's ledger sentence) is `CLOSED_MONTH_ACTION`, kept separate so
 * this fact stays inside the card's 20-word face. Reopening a month is a
 * manual change in the company's books, not an action this console offers,
 * and the card does not invent one.
 */
export function closedMonthSentence(item: QueueItem, role: StaffRole): string {
  // Decision 2's next step lives in `CLOSED_MONTH_ACTION`, so this fact stays
  // short enough for the card's face. The signature keeps `role` because
  // callers and pins pass it; the fact is the same for both roles.
  void role;
  const month = closedMonthName(item);
  return month === null
    ? CLOSED_MONTH_HEAD
    : `${month} is closed, so this payment cannot be booked yet.`;
}

/**
 * The card cannot be answered here; the fix happens somewhere else.
 *
 * This is exactly the population whose chip reads "Setup fix" (or "Month
 * closed" for a month). For a wallet card that is every reason with no book
 * action at all (`isBlockedMoovReason`, the same predicate the single card and
 * the bulk Book already ask); for a receipt or bill hold it is the blocked
 * ones; for a blocker it is the four families with no control on the card.
 */
export function isSetupFixCard(
  item: QueueItem,
  options: { hasSelectableLocations?: boolean } = {},
): boolean {
  if (isMoov(item)) {
    const code = (item.body ?? "").trim();
    // Round 5: a planless reason is classified, not generic — a books-side
    // setup fix or a closed month IS a setup fix (in the group); a system
    // wait or a person's decision is not.
    const cls = MOOV_PLANLESS_CLASS[code];
    if (cls !== undefined) return cls.state === "setup" || cls.state === "month";
    // An unreadable reason is not a setup fix: the card says "This needs a
    // look" and asks a person, which is what it already offered.
    if (!Object.hasOwn(MOOV_PLANS, code)) return false;
    // Nor is a reason whose card asks a person to look at the movement.
    if (MOOV_NEEDS_LOOK_CODES.has(code)) return false;
    // A wallet card asking for a location is answerable while there is one to
    // pick; with none it cannot be booked at all (round-1 review, item 3).
    if (code === "location_not_chosen") return options.hasSelectableLocations === false;
    return isBlockedMoovReason(code);
  }
  if (isHoldApproveWithheld(item)) return true;
  const subject = item.subject ?? "";
  return SETUP_FIX_BLOCKER_PREFIXES.some((prefix) => subject.startsWith(prefix));
}

/**
 * Blocker families whose cause is a bank line with no account attached —
 * `Missing Plaid account_id` and `Unmapped Plaid account`. Nothing on the
 * console fixes either one, so the card offers no hand-off: not "Ask the
 * owner" and not "Send to practice" (round-3 review, item 3, both roles).
 * Subject-keyed exactly like `presentBlocker`'s own branches.
 */
export function isMappingBlockerCard(item: QueueItem): boolean {
  const subject = item.subject ?? "";
  return (
    subject.startsWith(BLOCKER_PREFIX.missingAccount) ||
    subject.startsWith(BLOCKER_PREFIX.unmapped)
  );
}

function draft(partial: Draft): Draft {
  return partial;
}

function presentBlocker(item: QueueItem, ctx: CardPresenterContext): Draft {
  const subject = item.subject ?? "";
  const money = moneyParts(item.amount);
  const month = monthInSubject(subject) ?? "This month";
  const details = [`Subject: ${scrubLoose(subject)}`, ...commonDetails(item)];
  if (item.reason === "escrow-balance" || subject.startsWith(BLOCKER_PREFIX.escrow)) {
    // R4-25 (round-2 review): no control on this card records a payment, so the
    // face no longer tells the reader to. The fact it was making is kept.
    //
    // Round-3 review, MUST-FIX 1: the old fact — "shows a payment the books do
    // not have" — is only true for `direction=ledger_higher`. It is inverted for
    // `ledger_lower` and unknown for `uncheckable`, and the card DOES carry a
    // Record disbursement control. So the face states the one thing true of
    // every direction: the two figures disagree, or one could not be read.
    return draft({
      title: "Escrow money does not match the books",
      happened: ESCROW_BALANCE_FACE_COPY,
      suggest: "This card stays until the books agree.",
      primaryLabel: "Nothing to approve",
      secondary: secondaryFor(ctx.role, true),
      chip: needsYou(),
      family: "escrow-balance",
      groupKey: slugKey(item.company, `escrow-balance|${scrubLoose(subject)}`),
      disabledHint: "",
      details,
    });
  }
  if (subject.startsWith(BLOCKER_PREFIX.stale)) {
    return draft({
      title: "A published statement is out of date",
      happened: "Confirming a replacement does not clear this.",
      suggest: forRole(
        ctx.role,
        REVIEW_STATEMENT_AM,
        "Review the published statement.",
      ),
      primaryLabel: forRole(ctx.role, ASK_OWNER_LABEL, "Review the published statement"),
      secondary: secondaryFor(ctx.role, false),
      chip: waitingOwner(ctx.role),
      family: "stale-publication",
      groupKey: slugKey(item.company, `stale-publication|${scrubLoose(subject)}`),
      disabledHint: "There is nothing to approve.",
      details,
    });
  }
  if (item.reason === "statement-blocked" || subject.startsWith(BLOCKER_PREFIX.statement)) {
    // R4-25 (round-2 review): the face was an instruction this card has no
    // control for ("Confirm the statement…"). Both sentences are facts now,
    // and the second names the page that really does the confirming, in the
    // nav's own words ("Statement checks"). The owner-only confirm control is
    // on that page, so neither role is told to press something here.
    return draft({
      title: "A statement is blocking the month",
      happened: "This month is waiting on a statement that is not confirmed.",
      suggest:
        `Nothing on this card confirms the statement. ${navLabel("statements")} is where statements are confirmed.`,
      primaryLabel: "Show what is blocking",
      secondary: secondaryFor(ctx.role, true),
      chip: needsYou(),
      family: "statement",
      groupKey: slugKey(item.company, `statement|${scrubLoose(subject)}`),
      disabledHint: "",
      details,
    });
  }
  if (subject.startsWith(BLOCKER_PREFIX.monthClose)) {
    return draft({
      title: `${month} cannot be closed yet`,
      happened: "Something is still unanswered.",
      suggest: "Nothing on this card can close the month.",
      primaryLabel: "Show what is blocking",
      secondary: secondaryFor(ctx.role, true),
      chip: needsYou(),
      family: "month-close",
      groupKey: slugKey(item.company, `month-close|${month}`),
      disabledHint: "",
      details,
    });
  }
  if (subject.startsWith(BLOCKER_PREFIX.periodReopened)) {
    return draft({
      title: `${month} has to be closed again`,
      happened: "The month was reopened, so the close is not finished.",
      suggest: "Nothing on this card closes the month again.",
      primaryLabel: "Show what is blocking",
      secondary: secondaryFor(ctx.role, true),
      chip: needsYou(),
      family: "period-reopened",
      groupKey: slugKey(item.company, `period-reopened|${month}`),
      disabledHint: "",
      details,
    });
  }
  if (subject.startsWith(BLOCKER_PREFIX.closedWrite)) {
    return draft({
      title: "A closed month blocked a change",
      happened: closedMonthSentence(item, ctx.role),
      suggest: CLOSED_MONTH_ACTION,
      primaryLabel: "Show what is blocking",
      secondary: secondaryFor(ctx.role, false),
      chip: waitingOwner(ctx.role),
      family: "closed-period-write",
      groupKey: slugKey(item.company, `closed-period-write|${scrubLoose(subject)}`),
      disabledHint: "",
      details,
    });
  }
  if (subject.startsWith(BLOCKER_PREFIX.unmapped)) {
    // R4-26 / the owner's decision on the first bank account of a new company:
    // one sentence for both roles. Neither person can connect the account from
    // the console, so the card neither asks one of them to do it nor tells the
    // other to ask. It also keeps no "Ask the owner" chip or primary label.
    return draft({
      title: "A bank account is not connected to the books",
      happened: "Lines on this bank account are on hold.",
      suggest: FIRST_BANK_MAPPING_COPY,
      primaryLabel: "Nothing to approve",
      secondary: secondaryFor(ctx.role, false),
      chip: setupFixChip(),
      family: "unmapped-account",
      groupKey: slugKey(item.company, `unmapped-account|${scrubLoose(subject)}`),
      disabledHint: "There is nothing to approve.",
      details,
    });
  }
  if (subject.startsWith(BLOCKER_PREFIX.missingAccount)) {
    // Round-2 review, MUST-FIX 11, corrected by round-3 review MUST-FIX 2: this
    // blocker is ONE bank line that arrived with no account on it. Connecting an
    // account never clears it, so it must not borrow the first-bank sentence —
    // that sentence is true only of `Unmapped Plaid account` below, where
    // connecting really is the fix. Neither role can fix this from the console
    // either way, so the card states the fact and offers no control.
    return draft({
      title: "A bank line has no account on it",
      happened: MISSING_ACCOUNT_HAPPENED,
      suggest: MISSING_ACCOUNT_FACE_COPY,
      primaryLabel: "Nothing to approve",
      secondary: secondaryFor(ctx.role, false),
      chip: setupFixChip(),
      family: "missing-account-id",
      groupKey: slugKey(item.company, `missing-account-id|${scrubLoose(subject)}`),
      disabledHint: "There is nothing to approve.",
      details,
    });
  }
  if (subject.startsWith(BLOCKER_PREFIX.conflict)) {
    return draft({
      title: "Two receipts want the same bank line",
      happened: HOLD_SENTENCE.conflictClearsItself,
      suggest: HOLD_SENTENCE.waitForConflict,
      primaryLabel: "Show what is blocking",
      secondary: secondaryFor(ctx.role, false),
      chip: waitingSystem(),
      family: "receipt-match-conflict",
      groupKey: slugKey(item.company, `receipt-match-conflict|${scrubLoose(subject)}`),
      disabledHint: "There is nothing to approve.",
      details,
    });
  }
  if (subject.startsWith(BLOCKER_PREFIX.hubBatch)) {
    return presentHubBatch(item, ctx, subject, details);
  }
  if (subject.startsWith(BLOCKER_PREFIX.hubRefused)) {
    return draft({
      title: "A books update was refused",
      happened: "Nothing from that update was stored.",
      suggest: "A later accepted update for this month clears this.",
      primaryLabel: "Show what is blocking",
      secondary: secondaryFor(ctx.role, false),
      chip: waitingSystem(),
      family: "hub-gl-push-refused",
      groupKey: slugKey(item.company, `hub-gl-push-refused|${scrubLoose(subject)}`),
      disabledHint: "There is nothing to approve.",
      details,
    });
  }
  if (subject.startsWith(BLOCKER_PREFIX.hardMatch)) {
    return draft({
      title: "A receipt match was refused",
      happened: HOLD_SENTENCE.keepBankLine,
      suggest: HOLD_SENTENCE.keepingBankLineClears,
      primaryLabel: "Keep the bank line",
      secondary: secondaryFor(ctx.role, true),
      chip: needsYou(),
      family: "hard-match-refuse",
      groupKey: slugKey(item.company, `hard-match-refuse|${scrubLoose(subject)}`),
      disabledHint: "",
      details,
    });
  }
  if (subject.startsWith(BLOCKER_PREFIX.matchReview)) {
    return draft({
      title: "Confirm this receipt match",
      happened: HOLD_SENTENCE.doesNotClearAlone,
      suggest: HOLD_SENTENCE.confirmOrPickAnother,
      primaryLabel: "Show the candidates",
      secondary: secondaryFor(ctx.role, true),
      chip: needsYou(),
      family: "receipt-match-review",
      groupKey: slugKey(item.company, `receipt-match-review|${scrubLoose(subject)}`),
      disabledHint: "",
      details,
    });
  }
  void money;
  return draft({
    title: "This needs a look",
    happened: "Something is still unanswered.",
    suggest: forRole(ctx.role, ASK_LOOK_AT_THIS_AM, "Review this yourself."),
    primaryLabel: "Show what is blocking",
    secondary: secondaryFor(ctx.role, false),
    chip: needsYou(),
    family: "attention",
    groupKey: slugKey(item.company, `attention|${scrubLoose(subject)}`),
    disabledHint: "",
    details,
  });
}

function presentHubBatch(
  item: QueueItem,
  ctx: CardPresenterContext,
  subject: string,
  details: string[],
): Draft {
  const kind = unmatchedHubCensusKind(item.body ?? "");
  const happened =
    kind === "zero"
      ? "This batch adds up to zero, so there is no deposit to match."
      : kind === "awaiting_candidate"
        ? "Nothing has arrived yet that can match this batch."
        : kind === "no_remaining_candidate"
          ? "No deposit currently remains that can match this batch."
          : kind === "remaining_candidate_exists"
            ? "This can clear itself when a deposit matches it."
            : "This batch could not be read. Do not assume it will clear.";
  const waiting =
    kind === "awaiting_candidate" ||
    kind === "no_remaining_candidate" ||
    kind === "remaining_candidate_exists";
  // Round-11 decision 2 (owner, verbatim): "Just the company name is fine
  // (maple-rentals) has no matching deposit." The words come from the one
  // directory (B1c-1); an unknown name reads "this company" (decision 14), and
  // the slug is never shown.
  const directory = presenterDirectory(ctx);
  return draft({
    title: `${face.company(item.company, directory, "sentence")} has no matching deposit`,
    happened,
    suggest: waiting
      ? "This clears only when a deposit actually matches it."
      : "Do not assume this will clear on its own.",
    primaryLabel: "Show what is blocking",
    secondary: secondaryFor(ctx.role, false),
    chip: waiting ? waitingSystem() : needsYou(),
    family: "unmatched-hub-gl",
    groupKey: slugKey(item.company, `unmatched-hub-gl|${scrubLoose(subject)}|${kind}`),
    disabledHint: "There is nothing to approve.",
    details,
  });
}

function counterpartyName(item: QueueItem): string {
  const merchant = visibleBank(item.merchant).trim();
  if (merchant.length > 0) return merchant;
  const cleaned = amVisibleMultilineDisplayText(item.aiRationale ?? "");
  const to = /^to:(.+)$/m.exec(cleaned)?.[1]?.trim() ?? "";
  const from = /^from:(.+)$/m.exec(cleaned)?.[1]?.trim() ?? "";
  if (to.length > 0) return visibleBank(to).trim();
  if (from.length > 0) return visibleBank(from).trim();
  return "this counterparty";
}

function presentUntracked(item: QueueItem, ctx: CardPresenterContext): Draft {
  const company = face.company(item.company, presenterDirectory(ctx), "sentence");
  const name = counterpartyName(item);
  const money = moneyParts(item.amount);
  const moved = money.label ?? "Money";
  return draft({
    title: `${moved} moved between ${company} and "${name}"`,
    happened: `We do not recognise "${name}" as one of your companies.`,
    suggest: "Pick whether this is an outside party, a new company, or one you already keep books for.",
    primaryLabel: "Mark as external",
    secondary: ["Create company", "This is an existing company"],
    chip: needsYou(),
    family: "untracked-counterparty",
    groupKey: groupFor(item, "untracked-counterparty", money),
    disabledHint: "",
    details: commonDetails(item),
  });
}

function presentRemoval(item: QueueItem, ctx: CardPresenterContext): Draft {
  const money = moneyParts(item.amount);
  const recoverable = Boolean(item.suggestedAccount?.trim());
  if (recoverable) {
    return draft({
      title: "The bank says this never happened",
      happened: "Reverse it only if the original entry should come out.",
      suggest: "Reversing mirrors the original entry. You cannot change the account.",
      primaryLabel: "Reverse it",
      secondary: forRole(
        ctx.role,
        `Decline|${ASK_OWNER_LABEL}`,
        "Decline|Skip for now",
      ).split("|"),
      chip: needsYou(),
      family: "removal",
      groupKey: groupFor(item, "removal", money),
      disabledHint: "",
      details: commonDetails(item),
    });
  }
  return draft({
    title: "The bank removed a transaction we cannot identify",
    happened: "A reversal cannot be built from this card.",
    suggest: forRole(
      ctx.role,
      REMOVAL_ESCALATE_AM,
      "Decline to close it with no reversal.",
    ),
    primaryLabel: forRole(ctx.role, ASK_OWNER_LABEL, "Decline"),
    secondary: forRole(ctx.role, "Decline|Skip for now", "Skip for now").split("|"),
    chip: needsYou(),
    family: "removal",
    groupKey: groupFor(item, "removal", money),
    disabledHint: "",
    details: commonDetails(item),
  });
}

function presentRecovery(item: QueueItem, ctx: CardPresenterContext): Draft {
  const cause = bookingFailureCause(item.aiRationale);
  const money = moneyParts(item.amount);
  return draft({
    title: "Your last answer could not be booked",
    happened: cause !== null && cause.length > 0 ? cause : "Nothing was booked.",
    suggest: forRole(ctx.role, CHOOSE_AGAIN_AM, "Choose again."),
    primaryLabel: "Choose again",
    secondary: secondaryFor(ctx.role, true),
    chip: needsYou(),
    family: "booking-failed",
    groupKey: groupFor(item, "booking-failed", money),
    disabledHint: ACCOUNT_HINT,
    details: commonDetails(item),
  });
}

function presentUnmatched(item: QueueItem, ctx: CardPresenterContext): Draft {
  const money = moneyParts(item.amount);
  const complete = Boolean(item.suggestedAccount?.trim()) && Boolean(item.suggestedLocation?.trim());
  return draft({
    title: "Split this deposit the way it was sent?",
    happened: "Booking records the stored split. One account books the whole deposit instead.",
    suggest: "Nothing is booked until you decide.",
    primaryLabel: complete ? "Approve the split" : "Pick an account",
    secondary: secondaryFor(ctx.role, true),
    chip: needsYou(),
    family: "unmatched-deposit",
    groupKey: groupFor(item, "unmatched-deposit", money),
    disabledHint: complete ? "" : ACCOUNT_HINT,
    details: commonDetails(item),
  });
}

function presentNewVendor(item: QueueItem, ctx: CardPresenterContext): Draft {
  const money = moneyParts(item.amount);
  const merchant = visibleBank(item.merchant).trim();
  const title = merchant.length > 0 ? `What was ${merchant}?` : "What was this?";
  return draft({
    title,
    happened: "This payee is new. Pick the account. Nothing is booked until you do.",
    suggest: "Nothing is booked until you name the account.",
    primaryLabel: "Pick the account",
    secondary: secondaryFor(ctx.role, true),
    chip: needsYou(),
    family: "new-vendor",
    groupKey: groupFor(item, "new-vendor", money),
    disabledHint: ACCOUNT_HINT,
    details: commonDetails(item),
  });
}

function presentReceipt(item: QueueItem, ctx: CardPresenterContext): Draft {
  const money = moneyParts(item.amount);
  const hasAccount = Boolean(item.suggestedAccount?.trim());
  return draft({
    title: "Book this pair between companies?",
    happened: "The receipt is not attached to the property on this charge.",
    suggest: "Nothing is booked until you say which account to use.",
    primaryLabel: hasAccount ? "Book the pair" : "Pick the account",
    secondary: secondaryFor(ctx.role, true),
    chip: needsYou(),
    family: "receipt",
    groupKey: groupFor(item, "receipt", money),
    disabledHint: hasAccount ? "" : ACCOUNT_HINT,
    details: commonDetails(item),
  });
}

function presentBank(item: QueueItem, ctx: CardPresenterContext): Draft {
  const money = moneyParts(item.amount);
  const account = item.suggestedAccount?.trim();
  const location = item.suggestedLocation?.trim();
  const leaf = suggestedAccountWords(item, ctx, account);
  const judgment = account !== undefined && account.length > 0 && isJudgmentClassAccount(account);
  const complete = account !== undefined && account.length > 0 && location !== undefined && location.length > 0;
  const details = commonDetails(item);
  if (judgment) {
    return draft({
      title: "This needs a person to confirm",
      happened: "This would go to an owner, loan or company-to-company account.",
      suggest: "Nothing is booked until you confirm the account.",
      primaryLabel: "Confirm the account",
      secondary: secondaryFor(ctx.role, true),
      chip: needsYou(),
      family: "bank",
      groupKey: groupFor(item, "bank", money),
      disabledHint: complete ? "" : ACCOUNT_HINT,
      details,
    });
  }
  if (complete && leaf !== null && !isRecovery(item)) {
    return draft({
      title: `Is this ${leaf}?`,
      happened: `We think ${leaf}, but the bank text alone is not enough to book it ourselves.`,
      suggest: `${leaf} (fairly sure). Nothing is booked until you say yes.`,
      primaryLabel: `Yes, book to ${leaf}`,
      secondary: secondaryFor(ctx.role, true),
      chip: needsYou(),
      family: "bank",
      groupKey: groupFor(item, "bank", money),
      disabledHint: "",
      details,
    });
  }
  const why = evidenceQueueWhy(item.aiRationale);
  const happened = account !== undefined && account.length > 0 && (location === undefined || location.length === 0)
    ? "We think we know the account, but the location is still missing."
    : why !== null
      ? evidenceSentence(why)
      : BANK_TEXT_NOT_ENOUGH_COPY;
  const needsLocation = account !== undefined && account.length > 0;
  return draft({
    title: "What was this?",
    happened,
    suggest: "Nothing is booked until you name the account.",
    primaryLabel: needsLocation ? "Pick the location" : "Pick the account",
    secondary: secondaryFor(ctx.role, true),
    chip: needsYou(),
    family: "bank",
    groupKey: groupFor(item, "bank", money),
    disabledHint: needsLocation ? "Pick the location." : ACCOUNT_HINT,
    details,
  });
}

interface HoldActionFacts {
  hasAccount: boolean;
  complete: boolean;
  leaf: string | null;
  judgment: boolean;
  /** The card tells the person to look first (receipt not confirmed / did not book). */
  checkFirst: boolean;
}

/**
 * The button wording for a hold card, using the rules presentBank and
 * presentReceipt already use: a complete suggestion may say "Yes, book to
 * <leaf>" (only when the row is book_suggested), a known account with no
 * location asks for the location, and a judgment account asks for a person.
 */
function holdAction(
  wording: HoldWording,
  button: HoldWording["button"],
  facts: HoldActionFacts,
): { primaryLabel: string; disabledHint: string } {
  if (wording.blocked) {
    // Q-d's A2 blocked rendering replaces the approve control entirely. A
    // bill card is not a Moov card, so that control lives in queue.ts; this
    // face keeps a neutral, non-Book label and says what has to happen first.
    return {
      primaryLabel: "Pick the account",
      disabledHint: "This month is closed, so this cannot be booked.",
    };
  }
  if (wording.button === "book_pair") {
    // R12's account is an intercompany pair, so the judgment predicate would
    // always fire. presentReceipt's own rule is hasAccount-only.
    return facts.hasAccount
      ? { primaryLabel: "Book the pair", disabledHint: "" }
      : { primaryLabel: "Pick the account", disabledHint: ACCOUNT_HINT };
  }
  if (facts.judgment) {
    return {
      primaryLabel: "Confirm the account",
      disabledHint: facts.complete ? "" : ACCOUNT_HINT,
    };
  }
  switch (button) {
    case "book_suggested":
      if (facts.complete && facts.leaf !== null) {
        return { primaryLabel: `Yes, book to ${facts.leaf}`, disabledHint: "" };
      }
      return facts.hasAccount
        ? { primaryLabel: "Pick the location", disabledHint: "Pick the location." }
        : { primaryLabel: "Pick the account", disabledHint: ACCOUNT_HINT };
    case "pick_location":
      return { primaryLabel: "Pick the location", disabledHint: "Pick the location." };
    case "confirm_account":
      return {
        primaryLabel: "Confirm the account",
        disabledHint: facts.complete ? "" : ACCOUNT_HINT,
      };
    case "book_pair":
      return facts.hasAccount
        ? { primaryLabel: "Book the pair", disabledHint: "" }
        : { primaryLabel: "Pick the account", disabledHint: ACCOUNT_HINT };
    case "pick_account":
    case "none":
    default:
      if (facts.hasAccount && !facts.complete) {
        return { primaryLabel: "Pick the location", disabledHint: "Pick the location." };
      }
      // A complete suggestion on a card that does not ask for a check is a
      // one-tap booking, so the button says so (round 10, C-1). The check-first
      // cards keep the ask and stay disabled until a person chooses.
      if (facts.complete && facts.leaf !== null && !facts.checkFirst) {
        return { primaryLabel: `Yes, book to ${facts.leaf}`, disabledHint: "" };
      }
      return { primaryLabel: "Pick the account", disabledHint: ACCOUNT_HINT };
  }
}

function presentHold(
  item: QueueItem,
  code: HoldReasonCode,
  ctx: CardPresenterContext,
): Draft {
  const wording = HOLD_WORDING[code];
  const money = moneyParts(item.amount);
  const account = item.suggestedAccount?.trim();
  const location = item.suggestedLocation?.trim();
  const leaf = suggestedAccountWords(item, ctx, account);
  const hasAccount = account !== undefined && account.length > 0;
  const judgment = hasAccount && isJudgmentClassAccount(account);
  const complete =
    hasAccount && location !== undefined && location.length > 0 && leaf !== null;
  // R14 is book_suggested only when a rule already named a complete pair.
  const button =
    code === "receipt_needed_for_vendor" && complete
      ? "book_suggested"
      : wording.button;
  const action = holdAction(wording, button, {
    hasAccount,
    complete,
    leaf,
    judgment,
    checkFirst: HOLD_CHECK_FIRST_CODES.has(code),
  });
  const family = code.startsWith("bill_") ? "bill-hold" : "receipt-hold";
  const closedMonth = code === "bill_period_locked";
  return draft({
    title: wording.title,
    happened: closedMonth ? closedMonthSentence(item, ctx.role) : holdHappened(code, item),
    suggest: closedMonth
      ? CLOSED_MONTH_ACTION
      : forRole(ctx.role, wording.amNext, wording.ownerNext),
    primaryLabel: action.primaryLabel,
    secondary: secondaryFor(ctx.role, !wording.blocked),
    chip: wording.blocked ? waitingOwner(ctx.role) : needsYou(),
    family,
    groupKey: groupFor(item, family, money),
    disabledHint: closedMonth ? "" : action.disabledHint,
    details: [`Reason: ${code}`, ...commonDetails(item)],
  });
}

/**
 * The holds that tell the person to look at the receipt before booking (round
 * 10, C-1). Their primary control must not book the AI's guess: the account
 * fields are the offer, and Book stays disabled until a person chooses.
 */
const HOLD_CHECK_FIRST_CODES: ReadonlySet<HoldReasonCode> = new Set([
  "receipt_not_confirmed",
  "receipt_did_not_book",
]);

/**
 * True when the hold a card is wearing asks the person to check the receipt or
 * its card/category before an account can be booked. The presenter prints the
 * ask; the queue keeps the Book control disabled until an account is chosen.
 */
export function holdRequiresAccountChoice(item: QueueItem): boolean {
  const code = resolveHoldCode(item);
  return code !== null && HOLD_CHECK_FIRST_CODES.has(code);
}

/**
 * The receipt or bill hold a card is wearing, or null. The one resolution the
 * presenter and the queue both use, in the same order `present` tries its
 * arms: a wallet movement, blocker, untracked, removal or recovery card is
 * never a hold card, whatever its rationale says.
 */
export function resolveHoldCode(item: QueueItem): HoldReasonCode | null {
  if (isMoov(item)) return null;
  if (
    item.reason === "close-blocked" ||
    item.reason === "statement-blocked" ||
    item.reason === "escrow-balance"
  ) {
    return null;
  }
  if (item.reason === "untracked-counterparty") return null;
  if (isRemoval(item) || isRecovery(item)) return null;
  const holdEligible =
    item.reason === "low-confidence" ||
    item.reason === "anomaly" ||
    item.reason === "new-vendor" ||
    item.reason === "possible-personal" ||
    item.reason === "receipt_card_not_attached";
  if (!holdEligible) return null;
  const code = holdReasonFromRationale(item.aiRationale);
  if (code !== null) return code;
  if (
    item.receiptContext !== undefined &&
    (item.reason === "low-confidence" || item.reason === "new-vendor")
  ) {
    return item.receiptContext.matchVerified !== true
      ? "receipt_not_confirmed"
      : "receipt_did_not_book";
  }
  return null;
}

/**
 * The single question the single card and the bulk Book both ask of a receipt
 * or bill hold: is this one blocked, so no account choice can book it (a closed
 * month)? True means withhold every Book control. Mirrors
 * `isMoovApproveWithheld` for wallet cards.
 */
export function isHoldApproveWithheld(item: QueueItem): boolean {
  const code = resolveHoldCode(item);
  return code !== null && HOLD_WORDING[code].blocked;
}

function present(item: QueueItem, ctx: CardPresenterContext): Draft {
  if (isMoov(item)) return presentMoov(item, ctx);
  if (
    item.reason === "close-blocked" ||
    item.reason === "statement-blocked" ||
    item.reason === "escrow-balance"
  ) {
    return presentBlocker(item, ctx);
  }
  if (item.reason === "untracked-counterparty") return presentUntracked(item, ctx);
  if (isRemoval(item)) return presentRemoval(item, ctx);
  if (isRecovery(item)) return presentRecovery(item, ctx);
  const holdCode = resolveHoldCode(item);
  if (holdCode !== null) return presentHold(item, holdCode, ctx);
  if (item.reason === "unmatched-deposit") return presentUnmatched(item, ctx);
  if (item.reason === "new-vendor") return presentNewVendor(item, ctx);
  if (item.reason === "receipt_card_not_attached") return presentReceipt(item, ctx);
  if (
    item.reason === "low-confidence" ||
    item.reason === "anomaly" ||
    item.reason === "possible-personal"
  ) {
    return presentBank(item, ctx);
  }
  return draft({
    title: "This needs a look",
    happened: "This needs a person to look at it.",
    suggest: forRole(ctx.role, ASK_LOOK_AM, "Review this yourself."),
    primaryLabel: forRole(ctx.role, ASK_OWNER_LABEL, "Review it"),
    secondary: secondaryFor(ctx.role, false),
    chip: needsYou(),
    family: "attention",
    groupKey: slugKey(item.company, `attention|na`),
    disabledHint: "",
    details: commonDetails(item),
  });
}

function wordSafe(text: string, max: number, fallback: string): string {
  const clipped = clipWords(text, max);
  return clipped.length > 0 ? clipped : fallback;
}

export function cardPresentation(
  item: QueueItem,
  ctx: CardPresenterContext,
): CardPresentation {
  const hidden = hiddenIds(item);
  const built = present(item, ctx);
  const role = ctx.role;
  const memoryCents = moneyParts(item.amount).cents;
  const title = wordSafe(scrubFace(built.title, hidden), 15, "This needs a look");
  const happened = wordSafe(
    scrubFace(built.happened, hidden),
    20,
    "This needs a person to look at it.",
  );
  const suggestRaw = scrubFace(built.suggest, hidden);
  const suggest = suggestRaw.length > 0
    ? suggestRaw
    : forRole(role, ASK_LOOK_AM, "Review this yourself.");
  const primaryRaw = scrubFace(built.primaryLabel, hidden);
  const primaryLabel = primaryRaw.length > 0
    ? primaryRaw
    : forRole(role, ASK_OWNER_LABEL, "Review it");
  const secondary = built.secondary
    .map((label) => scrubFace(label, hidden))
    .filter((label) => label.length > 0);
  const chipText = scrubFace(built.chip.text, hidden);
  // One name for the state (Y5 brief item 3). A card the console cannot
  // answer is a "Setup fix"; a month holding the card is "Month closed".
  // The tone is left as the presenter chose it, so the card stays in the one
  // tile it was already counted in (no second tone, no CSS change).
  const setupFix = isSetupFixCard(item, {
    ...(ctx.hasSelectableLocations === undefined
      ? {}
      : { hasSelectableLocations: ctx.hasSelectableLocations }),
  });
  const stateChip = isClosedMonthCard(item)
    ? MONTH_CLOSED_CHIP
    : setupFix
      ? SETUP_FIX_CHIP
      : "";
  return {
    title,
    happened,
    suggest,
    primaryLabel,
    secondary,
    chip: {
      text: stateChip !== "" ? stateChip : chipText.length > 0 ? chipText : "Needs you",
      // A setup card is not waiting on the person, so it is counted in the
      // tile for the state, never under "Needs you" (one state per card).
      tone: stateChip !== "" ? "waiting on the owner" : built.chip.tone,
    },
    family: built.family,
    groupKey: scrubLoose(built.groupKey),
    memoryKey: scrubLoose(
      `${built.groupKey}|${memoryCents === null ? "na" : String(memoryCents)}`,
    ),
    disabledHint: scrubFace(built.disabledHint, hidden),
    details: built.details.map((line) => scrubLoose(line)).filter((line) => line.length > 0),
  };
}
